import mdsRequire from 'dojo/require';
import topic from 'dojo/topic';
import lang from 'dojo/_base/lang';
import on from 'dojo/on';
import domClass from 'dojo/dom-class';
import domAttr from 'dojo/dom-attr';
import dojoDate from 'dojo/date/locale';
import domConstruct from 'dojo/dom-construct';
import type { EntityViewTypes } from "../moreCore/UI/EntityBased/EntityView/EntityViewTypes"; // type-only: breaks the Helpers->EntityViewTypes ESM cycle edge
import { TabContainer, _TabEnabledMixin } from "../moreCore/_loadingHelpers/Tabbing"

// bitte dojo widgets ueber lazy loading importieren, ansonsten ist die webpack runtime ge-bricked, siehe provideFile() wo mit require das widget geladen wird

export namespace EntityHelpers {
    /**
     * Fragt um eine Entityview an via globalem topic.publish
     * Das erzeugte Entityview-Widget kann kann im topic '/Entity/Opened/<ID von Entity>' empfangen werden
     * @param data
     * @param furtherOptions Weitere Konfiguration; das openCallback liefert das Entityview-Widget retour, die anderen Callbacks heften sich direkt an die Emitter der Entityview
    */
    export function openEntityView(data: EntityViewTypes.setupConfig,
        furtherOptions?: {
            openCallback?: (entityview: any) => void,
            changedCallback?: Function,
            deletedCallback?: Function,
            closedCallback?: Function
            loadedCallback?: Function
            clonedCallback?: Function
        }): void {

        let handle: dojo.Handle

        if (furtherOptions) {
            
            if (data?.entityId) {
                handle = topic.subscribe("/Entity/Opened/" + data.entityId, function (entityviewWidget) {
                    if (furtherOptions.openCallback) {
                        furtherOptions.openCallback(entityviewWidget);
                    }
                    if (furtherOptions.changedCallback) {
                        on(entityviewWidget, "change", furtherOptions.changedCallback);
                    }
                    if (furtherOptions.deletedCallback) {
                        on(entityviewWidget, "deleted", furtherOptions.deletedCallback);
                    }
                    if (furtherOptions.closedCallback) {
                        on(entityviewWidget, "closed", furtherOptions.closedCallback);
                    }
                    
                    if (furtherOptions.loadedCallback) {
                        on(entityviewWidget, "loaded", furtherOptions.loadedCallback);
                    }

                    if (furtherOptions.clonedCallback) {
                        on(entityviewWidget, "cloned", furtherOptions.clonedCallback);
                    }
                    
                    if (handle) {
                        try {
                            handle.remove();
                        } catch (ex) {

                        }
                    }
                });
            }
        }
        topic.publish("/Entity/OpenView", data);
    }

    export function getSimplifiedObject<T>(originalData: any, propertyPathsToKeep: Array<string>): T {
        if (!originalData) return null;

        let newEntity = {

        };

        for (let prop of propertyPathsToKeep) {
            let value = getComplexProperty(originalData, prop);

            // TODO: komplexe Pfade setzen??

            newEntity[prop] = lang.clone(value);
        }

        return newEntity as any;
    }
    export function getComplexProperty<T = any>(entity: Object, path: string): T {
        if (!entity) return null;

        if (typeof path == 'undefined') {
            return null;
        }
        if (path == null) {
            return null;
        }

        let pathParts = path.split('.'); // array mit dem komplexen Attributstring
        if (pathParts.length < 1) {
            return null;
        }

        try {
            let currentAttribute = pathParts.shift();
            if (typeof (entity[currentAttribute]) != "undefined") {

                let attrFound = true;
                let subEntity = entity[currentAttribute];

                if (pathParts.length >= 1) // es gibt unterebenen!
                {
                    if (typeof (subEntity) == "object") {

                        if ((subEntity as Date).getTime && (subEntity as Date).getUTCDate) {
                            // das ist ein Datum, weiter kommen wir also nicht
                            return subEntity;
                        }
                        else {

                            // die nächsten Ebenen zusammenbauen und rekursiv aufrufen

                            let subPath = pathParts.join(".");

                            //console.log("PitHelper getnextlevel", subAttributes);
                            return getComplexProperty(subEntity, subPath);
                        }
                    }
                }

                return subEntity;
            }
        } catch (ex) {

        }
        return null;
    }
}

export namespace TranslationHelpers {
    function _getTranslations(): Object {
        let result = window.translations;

        if (!result || !result.hasOwnProperty("MainTitle")) {
            // kein window.translations vorhanden? dann mal auf den localstorage zugreifen:
            try {
                let translationsStr = localStorage.getItem("ego_Translations");

                if (translationsStr) {
                    let data = JSON.parse(translationsStr);
                    if (typeof (data) == "object") {
                        result = data.translations;
                    }
                }
            } catch (ex) {
                console.warn("Translations from Storage failed", ex)
            }
        } else {
            if (!window.sessionInfo?.sessionData?.Session?.Id) _saveTranslationsToStorage();
        }

        return result;
    }

    async function _saveTranslationsToStorage() {
        try {
            let translationsStr = localStorage.getItem("ego_Translations");
            if (translationsStr) {
                // prüfen ob der bestehende Storage älter ist als x:
                let existing = JSON.parse(translationsStr);
                if (typeof (existing) == "object" && !!existing) {
                    if (existing.timestamp && existing.timestamp.getTime) {
                        let timestamp = existing.timestamp.getTime();
                        let now = new Date();
                        if (DateHelpers.getDiffMinutes(timestamp, now) < 30) return;
                    }
                }
            }
            let data = {
                translations: window.translations,
                timestamp: new Date()
            };
            localStorage.setItem("ego_Translations", JSON.stringify(data));
        } catch (ex) {
            console.warn("Translations to Storage failed", ex)

        }
    }

    export function translate(translationString: string, allowEmptyString?: boolean): string {
        let translations = _getTranslations();
        if (translations) {
            if (translations?.[translationString]) {
                return translations[translationString];
            } else if (typeof (translations?.[translationString]) != undefined) {
                if (allowEmptyString == true) return "";
            }
        }
        return "#" + translationString;
    }
    export function canTranslate(translationString: string, allowEmptyString?: boolean): boolean {
        if (translationString == "") {
            return false;
        }
        if (!translationString) return undefined;

        let translations = _getTranslations();

        if (translations) {
            let value = translations?.[translationString];
            if (!!value) return true;
            else if (typeof (value) == "undefined") {
                return false;
            }
            else if (value == "" || value == null) {
                return allowEmptyString;
            }
        }

        return false;
    }
    /**
     * Prüft, ob ein Text übersetzt werden kann, gibt andernfalls unverändert den Text zurück.
     */
    export function tryTranslate(translationString: string, allowEmptyString?: boolean, replacementString?: string): string {

        if (canTranslate(translationString, allowEmptyString)) {
            return translate(translationString, allowEmptyString);
        } else if (typeof (replacementString) == "string") return replacementString;

        return translationString;
    }
    //    export function getTranslation(translationString: string): string {
    //        return translate(translationString);
    //    }
}

export namespace FeatureHelpers {

    /**
    * Prüft die Customersettings auf hinterlegte Module und Features.
    * Gibt true zurück, wenn Modul (und ggf. Feature) vorhanden.
     * Versionsformat: 123456.0000, entsprechend Datum yyMMdd und einer fortlaufenden vierstelligen Nummer (zB. 251224.0100)
    * @param modulenameOrFeaturePath Modulname, oder ein Pfad aufgebaut aus "Modulname/Featurename" oder "Modulname/Featurename/Mindestversion"
    * @param feature Optional: Featurename innerhalb des Moduls, falls nicht bereits als Pfad im ersten Parameter deklariert
    * @param minVersion Mindestversionsnummer, die das Modul aufweisen muss.Achtung: Falls KEINE Version im Modul hinterlegt, gilt die Prüfung als fehlgeschlagen
    * @param maxVersion Höchste Versionsnummer, die das Modul aufweisen darf. Achtung: Falls KEINE Version im Modul hinterlegt, gilt die Prüfung als fehlgeschlagen!
    */
    export function hasFeature(modulenameOrFeaturePath: string, featurename?: string, minVersion?: number, maxVersion?: number): boolean {
        let moduleName = modulenameOrFeaturePath;
        let parts = modulenameOrFeaturePath?.split("/");
        if (parts && parts.length && parts.length == 1) {
            parts = modulenameOrFeaturePath?.split(".");
        }

        if (parts && parts.length >= 2) {
            moduleName = parts[0];
            featurename = parts[1];
            if (parts.length >= 3 && !minVersion) {
                try {
                    minVersion = parseFloat(parts[2]);

                    // wenn jemand doch noch versehentlich yyMMddHHmm angegeben hatt, statt yyMMdd.HHmm
                    if (minVersion > 999999) {
                        minVersion = minVersion / 10000; // 4 Stellen nach rechts rücken
                    }

                } catch (ex) {
                    console.warn("featurepath error: cannot parse minversion of path " + modulenameOrFeaturePath, ex);
                }
            }
        }

        if (!moduleName) return false;

        let module = getModule(moduleName);

        if (minVersion > 0 || maxVersion > 0) {
            if (!hasModuleVersion(moduleName, minVersion, maxVersion)) {
                module = null;
            }
        }

        if (!module) return false;

        if (featurename) {
            featurename = featurename.toLowerCase();
        }


        if (featurename) {
            for (let entryFeature of module.features) {
                if (entryFeature) entryFeature = entryFeature.toLowerCase();
                if (entryFeature == featurename) {
                    return true;
                }
            }

            return false;
        } else {
            return true;
        }

        return false;
    }

    interface moduleType { name: string, features: Array<string>, version: number };
    /**
     * Prüft die Customersettings auf Vorhandensein eines Moduls, und schränkt das Ergebnis ein auf eine Minimal- und/oder Maximalversionsnummer.
     * Versionsformat: 123456.0000, entsprechend Datum yyMMdd und einer fortlaufenden vierstelligen Nummer (zB. 251224.0100)
    * @param moduleName Modulname
    * @param minVersion Mindestversionsnummer, die das Modul aufweisen muss. Achtung: Falls KEINE Version im Modul hinterlegt, gilt die Prüfung als FEHLGESCHLAGEN!
    * @param maxVersion Höchste Versionsnummer, die das Modul aufweisen darf. Achtung: Falls KEINE Version im Modul hinterlegt, gilt die Prüfung als BESTANDEN!
     * @returns
     */
    export function hasModuleVersion(moduleName: string, minVersion?: number, maxVersion?: number): boolean {
        let module = getModule(moduleName);

        // wenn jemand doch noch versehentlich yyMMddHHmm angegeben hatt, statt yyMMdd.HHmm
        if (minVersion > 999999) {
            minVersion = minVersion / 10000; // 4 Stellen nach rechts rücken
        }
        if (maxVersion > 999999) {
            maxVersion = maxVersion / 10000; // 4 Stellen nach rechts rücken
        }

        if (module != null) {
            if (minVersion > 0 || maxVersion > 0) {
                if (module.version > 0) {
                    if (module.version > 999999) {
                        // falsches Format yyMMddHHmm => muss yyMMdd.HHmm sein!
                        module.version = module.version / 10000;
                    }
                    if (minVersion > module.version) module = null;
                    else if (maxVersion < module.version) module = null;
                }

                else module = null;
            }
        }


        return !!module;
    }

    /**
     * Holt aus einem Modul einen bestimmten Bestandteil, zB. ein bestimmtes Setting oder Settings-Objekt außerhalb der standardmäßig enthaltenen Features.
     * @param modulename Modulname in Customersettings
     * @param path Pfad zum Property oder Name des Properties innerhalb des Moduls
     */
    export function getModuleProperty<T>(modulename: string, path: string): Partial<T> {
        let module = getModule(modulename);
        if (module) {
            let result = EntityHelpers.getComplexProperty<T>(module, path);
            return result;
        }

        return null;
    }

    /**
     * Holt eine komplette Modulkonfiguration aus den Customersettings. Rückgabe: null, wenn Modul nicht vorhanden, ansonsten Objekt.
     */
    export function getModule<T>(modulename: string): Partial<moduleType & T> {
        if (!!modulename) {
            modulename = modulename.toLowerCase();
        } else {
            return null;
        }

        let result: moduleType & T = null;

        if (window.customerSettings?.modules) {
            for (let entry of window.customerSettings.modules) {
                let name = entry.name;
                name = name.toLowerCase();
                if (name == modulename) {
                    if (!result) {
                        result = {} as any;
                    } else {
                        console.warn("Module '" + modulename + "' is duplicate in config. Check customersettings in Appsettings!");
                    }

                    for (let key in entry) {

                        if (result.hasOwnProperty(key)) {
                            // gibts das Property schon?
                            let keyValue = entry[key];
                            if (lang.isArray(keyValue)) {
                                for (let val of keyValue) {
                                    result[key].push(lang.clone(val));
                                }
                            } else {
                                result[key] = lang.clone(entry[key]);
                            }

                        } else {
                            result[key] = lang.clone(entry[key]);
                        }
                    }
                }
            }
        }

        return result;
    }
    /**
     * Holt ein komplettes Customersetting-Property aus dem Settings-File. (Hinweis: 'modules' ist eines der Properties, wird aber besser mit getModule und hasFeature behandelt)
     */
    export function getCustomerSetting<T>(settingName: string): T {
        if (typeof (window.customerSettings) != "undefined") {
            if (window.customerSettings[settingName]) {
                return window.customerSettings[settingName];
            }
        }

        return null;
    }

    /**
     * Prüft auf Feature-Kollisionen in "customerSettings" im angegebenen Array.
     * Am besten in postCreate() oder beim startup() verwenden.
     * Bei Kollisionen gibt es einen alert() und detaillierte Informationen in der Konsole.
     * @param list Array<Array<string>>, jedes String-Array stellt einen Satz dar, aus dem nur eine Feature verfügbar sein sollte, zB: [["Feature1/Name1"], ["Feature1/Name2"], ["Feature2/Name1"], ["Feature2/Name2"]]
     */
    export function collisionsCheck(list: Array<Array<string>>) {
        let needAlert = false;

        list.forEach(featureList => {
            let collidingFeatures = [];

            featureList.forEach((feature) => {
                if (hasFeature(feature)) collidingFeatures.push(feature);
            })
            if (collidingFeatures.length > 1) {
                needAlert = true;
                console.error(`Für das angegebene Feature-Array: '${featureList}', sind die Kollisionen gefunden!\r\nKollidierende Features: '${collidingFeatures}'\r\nBitte überprüfen die Webtool-customerSettings, um das Problem zu beheben.`)
            }
        });

        if (needAlert) alert("Feature-Kollisionen gefunden! Weitere Informationen in der Konsole.");
    }

    /**
     * Prüft ob Feature-Abhängigkeiten in „customerSettings“ bestehen.
     * Am besten in postCreate() oder beim startup() verwenden.
     * Bei fehlenden Abhängigkeiten gibt es einen alert() und detaillierte Informationen in der Konsole.
     * @param list Array<{ mainFeature: string, featureList: Array<string> }>; mainFeature - Haupt-Feature, für die Abhängigkeiten geprüft werden. Wenn Feature in customerSettings nicht vorhanden ist, wird die Prüfung übersprungen (in der Konsole vermerkt); featureList - String-Array von Features, von denen Haupt-Feature abhängig ist, zB: [{mainFeature: "Feature/Name", featureList: ["Feature/Name1", "Feature/Name1"]}{mainFeature: "Feature1/Name1", featureList: ["Feature1/Name2", "Feature1/Name3"]}]
     */
    export function dependenciesCheck(list: Array<{ mainFeature: string, featureList: Array<string> }>) {
        let needAlert = false;

        list.forEach(entry => {
            if (!hasFeature(entry.mainFeature)) {
                console.warn(`Es wurde eine Abhängigkeitsprüfung durchgeführt, aber die Haupt-Feature: '${entry.mainFeature}' ist in den customerSettings nicht vorhanden. Bitte überprüfen die korrekte Konfiguration!`);
                return;
            }

            let missingFeatures = [];
            entry.featureList.forEach((feature) => {
                if (!hasFeature(feature)) missingFeatures.push(feature);
            });

            if (missingFeatures.length > 0) {
                needAlert = true;
                console.error(`Für das angegebene Haupt-Feature: '${entry.mainFeature}' fehlen einige Features!\r\nFehlende Features: '${missingFeatures}'\r\nBitte überprüfen die Webtool-customerSettings, um das Problem zu beheben.`)
            }
        });

        if (needAlert) alert("Fehlende Einträge in den customerSettings-Features! Weitere Informationen in der Konsole.");
    }
}

export namespace UrlHelpers {

    interface urlParamQueue {
        baseUrl: string,
        search: string,
        actions: Array<
            {
                type: "set" | "remove",
                paramName: string,
                paramValue?: string
            }
        >
    }

    interface exWindow extends Window {
        _replaceStateTimeout: number
        _urlParamQueue: urlParamQueue
        _urlParamQueueTimeout: number
    }

    function _getParameterQueue(): urlParamQueue {
        let windowEx: exWindow = window as any;
        if (!windowEx._urlParamQueue) {
            return {
                baseUrl: window.location.href,
                search: window.location.search,
                actions: []
            };
        } else return windowEx._urlParamQueue;
    }
    function _addUrlParamActionToQueue(type: "set" | "remove", name: string, value?: string) {
        let windowEx: exWindow = window as any;

        if (!name || !type) return;

        let queue = _getParameterQueue();

        // Warteschlange filtern: denselben Parameter nicht mehrfach reinnehmen
        let actionsWithoutParam = queue.actions.filter(entry => entry.paramName.toLowerCase() != name.toLowerCase());

        queue.actions = actionsWithoutParam;

        queue.actions.push({
            paramName: name,
            type: type,
            paramValue: value
        });

        windowEx._urlParamQueue = queue;

        if (windowEx._urlParamQueueTimeout) clearTimeout(windowEx._urlParamQueueTimeout);

        windowEx._urlParamQueueTimeout = setTimeout(_handleUrlParamQueue, 300);
    }

    function _handleUrlParamQueue() {
        let queue = _getParameterQueue();
        let windowEx: exWindow = window as any;
        windowEx._urlParamQueue = null;

        let newParamList: Array<{ name: string, value: string }> = [];

        let currentUrl = window.location.href; // gesamter Pfad
        let newUrl = currentUrl;
        let currentLocationSearchVal = window.location.search; // parameter: ?id=0000000019&comment=xyz
        let urlPathParts = location.pathname.split("/"); // /helpdesk/item/?id=0000000019 -> ["", helpdesk, item, ""]

        let urlParamStr = currentLocationSearchVal.slice(1); // führendes '?' entfernen
        // id=xyz&type=abc

        let existingParamsList = urlParamStr.split("&");
        // [id=xyz, type=abc]

        //console.debug("_handleUrlParamQueue", queue, existingParamsList, currentUrl, windowEx._urlParamQueue);

        // alle bestehenden Params durchlaufen
        for (let existingParamStr of existingParamsList) {
            // "comment=123123" - weiter aufdröseln:
            let paramParts = existingParamStr.split("=");

            let existingParamName = paramParts[0]; // comment
            let existingParamValue = paramParts?.length >= 2 ? paramParts[1] : null; // 123123

            if (existingParamName && existingParamValue) {
                newParamList.push({
                    name: existingParamName, value: existingParamValue
                });
            }
        }

        for (let action of queue.actions) {

            let targetParamName = action.paramName;
            let targetParamValue = action.paramValue;
            let searchIndex = -1;

            for (let i = 0; i < newParamList.length; i++) {
                let name = newParamList[i].name.toLowerCase();
                if (name == targetParamName.toLowerCase()) {
                    newParamList[i].value = targetParamValue;
                    searchIndex = i;
                    break;
                }
            }

            if (searchIndex != -1 && action.type == "remove") {
                newParamList.splice(searchIndex, 1);
            } else if (searchIndex == -1 && action.type == "set") {
                newParamList.push({ name: targetParamName, value: targetParamValue });
            }
        }


        let separator = "?";
        let newParamsstring = "";
        for (let entry of newParamList) {
            if (entry.value) {
                newParamsstring += separator + entry.name + "=" + entry.value;
                separator = "&";
            }
        }

        newUrl = currentUrl.replace(currentLocationSearchVal, "");
        newUrl += newParamsstring;

        window.history.replaceState('', '', newUrl);
    }

    function _delayReplaceState(newurl): void {


        let windowEx: exWindow = window as any;

        if (windowEx._replaceStateTimeout) clearTimeout(windowEx._replaceStateTimeout);
        windowEx._replaceStateTimeout = setTimeout(lang.hitch(this, function (newurl) {
            try {
                window.history.replaceState('', '', newurl);
            } catch (ex) {

            }
        }, newurl), 300);
    }

    export function getUrlAction(): false | string {
        let urlParams = location.pathname;
        //console.log("getUrlAction", urlParams);

        let action: false | string = false;

        if (urlParams != "" && urlParams != null && typeof (urlParams) != "undefined") {
            let pathParts = location.pathname.split("/");
            action = pathParts[pathParts.length - 1];
        }

        return action;
    }
    export function removeUrlAction(actionName: string): boolean {
        let currentAction = getUrlAction();

        //console.debug("removeUrlAction", "currentAction: ", currentAction, "currentUrl: " + window.location.href);

        if (currentAction == false) {
            return false;
        }

        if (currentAction.toUpperCase() != actionName.toUpperCase() && actionName != "" && actionName != null && typeof (actionName) != "undefined") {
            return false;
        }

        //console.debug("removeUrlAction currentLocation", currentlocation, "newLocation: " + newLocation);

        let url = window.location.pathname;
        let newurl = url.replace(currentAction, "");
        //console.debug("removeUrlAction", "new url: " + newurl);
        _delayReplaceState(newurl);

        return true;
    }
    export function setUrlAction(actionName: string, parameters: Array<any>): void {
        let currentAction = getUrlAction();
        //console.debug("setUrlAction", "currentAction", currentAction, "current location: " + location.pathname);

        if (currentAction != false) {
            removeUrlAction(currentAction);
        }

        let currentlocation = location.pathname;
        let newLocation = currentlocation + "/" + actionName;
        newLocation = newLocation.replace("//", "/");

        //console.debug("setUrlAction after remove", "currentlocation: " + currentlocation, "newLocation: " + newLocation);

        let url = window.location.pathname; // gesamter Pfad
        let newurl = url + actionName;
        newurl = newurl.replace("//", "/");
        //console.debug("setUrlAction newurl", newurl);
        _delayReplaceState(newurl);

        if (typeof (parameters) == "object") {
            for (let i = 0; i < parameters.length; i++) {
                let param = parameters[i];

                setUrlParameter(param.Name, param.Value);
            }
        }
    }
    export function getUrlParameterNames(): Array<string> {
        let urlParams = location.search;

        if (urlParams != "" && urlParams != null && typeof (urlParams) != "undefined") {
            let pathParts = location.pathname.split("/"); // /helpdesk/item/?id=0000000019 -> ["", helpdesk, item, ""]
            let action = pathParts[pathParts.length - 1]; // letzter Eintrag im Pfad, zb bei /helpdesk/item/?id=0000000019 wär das "",

            //console.debug("urlparams", urlParams, pathParts);

            let param = urlParams.slice(1);
            // id=xyz&type=abc

            let paramList = param.split("&");
            // [id=xyz, type=abc]

            let paramNames = [];

            // zb  /helpdesk/item/?id=0000000019&comment=123123
            // paramlist = [id=0000000019, comment=123123]
            for (let i = 0; i < paramList.length; i++) {
                let strParam = paramList[i].split("=");

                paramNames.push(strParam[0]);
            }

            return paramNames;
        }

        return [];
    }
    export function getUrlParameterValue(paramName: string): false | string {
        if (typeof (paramName) == "undefined" || paramName == "" || paramName == null) {
            return false;
        }

        // eigentlich müsst man nachschaun, ob der Parameter nicht schon zur Löschung vorgemerkt ist
        let queueEntry = _getParameterQueue().actions.filter(entry => entry.paramName.toLowerCase() == paramName.toLowerCase()).shift();
        if (queueEntry) {
            if (queueEntry.type == "remove") return false;
            // falls nicht zur Löschung, dann den zukünftigen neuen Wert verwenden:
            return queueEntry.paramValue;
        }

        // falls in der Queueu nichts ist, dann klassisch in der URL nachschauen:
        let urlParams = location.search;

        if (urlParams != "" && urlParams != null && typeof (urlParams) != "undefined") {
            let pathParts = location.pathname.split("/"); // /helpdesk/item/?id=0000000019 -> ["", helpdesk, item, ""]
            let action = pathParts[pathParts.length - 1]; // letzter Eintrag im Pfad, zb bei /helpdesk/item/?id=0000000019 wär das "",

            //console.debug("urlparams", urlParams, pathParts);

            // url im browser noch anpassen??
            //siteAction(action, urlParams);

            let param = urlParams.slice(1);
            // id=xyz&type=abc

            let paramList = param.split("&");
            // [id=xyz, type=abc]

            //console.log("siteAction", action, paramString, paramList);


            // zb  /helpdesk/item/?id=0000000019&comment=123123
            // paramlist = [id=0000000019, comment=123123]
            for (let i = 0; i < paramList.length; i++) {
                let paramsArray = paramList[i].split("=");
                //console.log(param);
                if (paramsArray[0].toUpperCase() == paramName.toUpperCase()) {
                    let paramValue = paramsArray[1];


                    //let removingEntry = queue.filter(entry => entry.paramName.toUpperCase() == paramName.toUpperCase())
                    if (paramValue) paramValue = decodeURIComponent(paramValue);
                    return paramValue;
                    break;
                }
            }
        }

        return false;
    }
    export function removeUrlParameter(paramName: string): boolean {
        if (typeof (paramName) == "undefined" || paramName == "" || paramName == null) {
            return false;
        }

        _addUrlParamActionToQueue("remove", paramName);

        return true;
    }
    export function setUrlParameter(paramName: string, paramValue: string): boolean {
        if (typeof (paramName) == "undefined" || paramName == "" || paramName == null) {
            return false;
        }

        if (typeof (paramValue) == "undefined" || paramValue == "" || paramValue == null) {
            return removeUrlParameter(paramName);
        }

        _addUrlParamActionToQueue("set", paramName, paramValue);

        return true;
    }
}

export namespace WindowHelpers {
    export function updateTopbarMargin(delay?: number): void {
    }


    /**
  * Setzt Fenstertitel (document.title) und Seitenüberschrift
  * @param windowTitle Fenstertitel. Es wird versucht, den Text mit stringFormat zu ergänzen um den MainTitle des Webtools ({0} in translations["MainTitle"])
  * @param topBar Seitenüberschrift, je nach grundsätzlichem Layout üblicherweise in der obersten fix sichtbaren Leiste. Wenn null: übernimmt den windowTitle. Wenn leer: Kein Titel
  */
    export function setTitles(windowTitle?: string, topBarTitle?: string): void {
        if (!windowTitle) {
            if (window.sidebar) {
                //windowTitle = window.sidebar.getSelectedSectionTitle();
            }
        }

        if (!topBarTitle) {
            topBarTitle = windowTitle;
        }

        let defaultMainTitle = TranslationHelpers.translate("MainTitle");
        let bDefaultMainTitleAvailable = TranslationHelpers.canTranslate("MainTitle")

        if (!windowTitle) {
            windowTitle = "";
            if (bDefaultMainTitleAvailable) {
                windowTitle = defaultMainTitle;
            }
            if (!topBarTitle) {
                topBarTitle = "";
            }
        } else {
            if (TranslationHelpers.canTranslate(windowTitle)) windowTitle = TranslationHelpers.translate(windowTitle);

            // es ist ein windowtitle übergeben worden. versuchen, den maintitle hineinzumappen:
            if (bDefaultMainTitleAvailable) {
                let newWindowTitle = StringHelpers.stringFormat(windowTitle, defaultMainTitle);

                // schauen ob sich was geändert hat dadurch
                if (newWindowTitle == windowTitle) {
                    // Titel hat sich nicht geändert, es war also kein Platzhalter für den MainTitle drin
                    // jetzt kann es aber sein, dass jemand bei setTitles schon selber ein stringFormat gemacht hat
                    // also prüfen, ob der MainTitle nicht eh scon im windowTitle steht
                    if (newWindowTitle.indexOf(defaultMainTitle, 0) == -1) {

                        // wenn nicht: den Fenstertitel selber hineinmappen
                        newWindowTitle = StringHelpers.stringFormat(windowTitle + " - {0}", defaultMainTitle);
                    }
                }

                windowTitle = newWindowTitle;
            } else {

            }
        }



        if (topBarTitle == null || topBarTitle == "") {
            topBarTitle = "";
        }

        let headingContainer = document.getElementById("mainMenuTitle");
        if (!headingContainer) headingContainer = document.getElementById("topBarTitle"); // neue Benennung ab 06-2024!
        if (headingContainer != null) {
            headingContainer.innerHTML = topBarTitle;
        }

        let productName = FeatureHelpers.getCustomerSetting("productName");
        if (!!productName) {
            document.title = productName + " " + windowTitle;
            //console.log("setTitles", id, windowTitle, productName);
        } else if (windowTitle) {
            document.title = windowTitle;
        }
    }
}

export namespace StringHelpers {
    export function stringFormat(template: string, ...args: any[]): string {
        template = TranslationHelpers.tryTranslate(template, true);
        return template.replace(/{(\d+)}/g, function (match, number) {
            return typeof args[number] != 'undefined'
                ? args[number]
                : match
                ;
        });
    }

    /**
     * Versucht aus einem String HTML-Elemente zu parsen (mit DOMparser, zuvor wird ein einfacher Regex-Test durchgeführt)
     * Gibt null zurück, falls kein HTML geparst werden kann.
     * @returns geparste HTML-Nodes
     */
    export function stringToElements(unknownString: string): HTMLElement[] {
        if (!unknownString) return null;
        let regex = /^<.+>$/;
        if (regex.test(unknownString) == false) return null;
        let elements: HTMLElement[] = [];
        try {
            let DOMparser = new DOMParser();

            let doc = DOMparser.parseFromString(unknownString, "text/html");
            
            doc.body.childNodes.forEach(entry => elements.push(entry as HTMLElement));
            //for (let i = 0; i < doc.body.childNodes.length; i++) {
            //    elements.push(doc.body.childNodes[i] as any);
            //}

        } catch (ex) {
            console.warn("stringToElements", ex, unknownString);
        }


        return elements;
    }


    export function isPitID(teststring: string): boolean {
        if (!teststring) return false;
        if (typeof (teststring) != "string") return false;
        try {
            if (teststring.length != 17) return false;
            let split = teststring.split(":");
            if (split.length != 2) return false;
            if (split[0].length != 8 || split[1].length != 8) return false;
            let parse1 = parseInt(split[0], 16);
            let parse2 = parseInt(split[1], 16)
            if (parse1 == 0 || parse2 == 0) return false;
            if (isNaN(parse1) || isNaN(parse2)) return false;

        } catch (ex) {
            return false;
        }
        return true;
    }

    /**
     * Konvertiert Zahl zu Text und fügt führende Nullen ein.
     * @param num
     * @param minWidth
     * @returns
     */
    export function numberToString(num: number, minWidth: number, precision?: number) {

        let str = num.toString();

        str = num.toLocaleString(navigator?.language, {
            minimumFractionDigits: precision ? precision : undefined,
            maximumFractionDigits: precision ? precision : undefined,
            minimumIntegerDigits: minWidth ? minWidth : 0
        })
        
        ///if (precision) str = num.toFixed(precision); // TODO: die Nachkommastellen zählen nicht zur width, wegkürzen, dann wieder dranpappen...
        //while (str.length < minWidth) str = "0" + str;
        return str;
    }

    export function randomID(length: number = 6): string {
        let factor = 10 * Math.pow(10, length);
        return (Math.random() * factor).toFixed(0);
    }
    export function formatBytes(bytes: number, decimals = 2) {
        return FileHelpers.formatBytes(bytes, decimals);
    }

    export function hashCode(str: string) {
        let hash = 0;
        if (!str) return "";
        for (let i = 0, len = str.length; i < len; i++) {
            let chr = str.charCodeAt(i);
            hash = (hash << 5) - hash + chr;
            hash |= 0; // Convert to 32bit integer
        }
        return hash;
    }
    export function hashMD5(string: string) {
        let hash = _hashHelperM(_hashHelperV(_hashHelperY(_hashHelperX(string), 8 * string.length)));
        return hash.toLowerCase()
    }

    function _hashHelperM(d) { for (var _, m = "0123456789ABCDEF", f = "", r = 0; r < d.length; r++)_ = d.charCodeAt(r), f += m.charAt(_ >>> 4 & 15) + m.charAt(15 & _); return f }
    function _hashHelperX(d) { for (var _ = Array(d.length >> 2), m = 0; m < _.length; m++)_[m] = 0; for (m = 0; m < 8 * d.length; m += 8)_[m >> 5] |= (255 & d.charCodeAt(m / 8)) << m % 32; return _ }
    function _hashHelperV(d) { for (var _ = "", m = 0; m < 32 * d.length; m += 8)_ += String.fromCharCode(d[m >> 5] >>> m % 32 & 255); return _ }
    function _hashHelperY(d, _) { d[_ >> 5] |= 128 << _ % 32, d[14 + (_ + 64 >>> 9 << 4)] = _; for (var m = 1732584193, f = -271733879, r = -1732584194, i = 271733878, n = 0; n < d.length; n += 16) { var h = m, t = f, g = r, e = i; f = _hashHelper_md5_ii(f = _hashHelper_md5_ii(f = _hashHelper_md5_ii(f = _hashHelper_md5_ii(f = _hashHelper_md5_hh(f = _hashHelper_md5_hh(f = _hashHelper_md5_hh(f = _hashHelper_md5_hh(f = _hashHelper_md5_gg(f = _hashHelper_md5_gg(f = _hashHelper_md5_gg(f = _hashHelper_md5_gg(f = _hashHelper_md5_ff(f = _hashHelper_md5_ff(f = _hashHelper_md5_ff(f = _hashHelper_md5_ff(f, r = _hashHelper_md5_ff(r, i = _hashHelper_md5_ff(i, m = _hashHelper_md5_ff(m, f, r, i, d[n + 0], 7, -680876936), f, r, d[n + 1], 12, -389564586), m, f, d[n + 2], 17, 606105819), i, m, d[n + 3], 22, -1044525330), r = _hashHelper_md5_ff(r, i = _hashHelper_md5_ff(i, m = _hashHelper_md5_ff(m, f, r, i, d[n + 4], 7, -176418897), f, r, d[n + 5], 12, 1200080426), m, f, d[n + 6], 17, -1473231341), i, m, d[n + 7], 22, -45705983), r = _hashHelper_md5_ff(r, i = _hashHelper_md5_ff(i, m = _hashHelper_md5_ff(m, f, r, i, d[n + 8], 7, 1770035416), f, r, d[n + 9], 12, -1958414417), m, f, d[n + 10], 17, -42063), i, m, d[n + 11], 22, -1990404162), r = _hashHelper_md5_ff(r, i = _hashHelper_md5_ff(i, m = _hashHelper_md5_ff(m, f, r, i, d[n + 12], 7, 1804603682), f, r, d[n + 13], 12, -40341101), m, f, d[n + 14], 17, -1502002290), i, m, d[n + 15], 22, 1236535329), r = _hashHelper_md5_gg(r, i = _hashHelper_md5_gg(i, m = _hashHelper_md5_gg(m, f, r, i, d[n + 1], 5, -165796510), f, r, d[n + 6], 9, -1069501632), m, f, d[n + 11], 14, 643717713), i, m, d[n + 0], 20, -373897302), r = _hashHelper_md5_gg(r, i = _hashHelper_md5_gg(i, m = _hashHelper_md5_gg(m, f, r, i, d[n + 5], 5, -701558691), f, r, d[n + 10], 9, 38016083), m, f, d[n + 15], 14, -660478335), i, m, d[n + 4], 20, -405537848), r = _hashHelper_md5_gg(r, i = _hashHelper_md5_gg(i, m = _hashHelper_md5_gg(m, f, r, i, d[n + 9], 5, 568446438), f, r, d[n + 14], 9, -1019803690), m, f, d[n + 3], 14, -187363961), i, m, d[n + 8], 20, 1163531501), r = _hashHelper_md5_gg(r, i = _hashHelper_md5_gg(i, m = _hashHelper_md5_gg(m, f, r, i, d[n + 13], 5, -1444681467), f, r, d[n + 2], 9, -51403784), m, f, d[n + 7], 14, 1735328473), i, m, d[n + 12], 20, -1926607734), r = _hashHelper_md5_hh(r, i = _hashHelper_md5_hh(i, m = _hashHelper_md5_hh(m, f, r, i, d[n + 5], 4, -378558), f, r, d[n + 8], 11, -2022574463), m, f, d[n + 11], 16, 1839030562), i, m, d[n + 14], 23, -35309556), r = _hashHelper_md5_hh(r, i = _hashHelper_md5_hh(i, m = _hashHelper_md5_hh(m, f, r, i, d[n + 1], 4, -1530992060), f, r, d[n + 4], 11, 1272893353), m, f, d[n + 7], 16, -155497632), i, m, d[n + 10], 23, -1094730640), r = _hashHelper_md5_hh(r, i = _hashHelper_md5_hh(i, m = _hashHelper_md5_hh(m, f, r, i, d[n + 13], 4, 681279174), f, r, d[n + 0], 11, -358537222), m, f, d[n + 3], 16, -722521979), i, m, d[n + 6], 23, 76029189), r = _hashHelper_md5_hh(r, i = _hashHelper_md5_hh(i, m = _hashHelper_md5_hh(m, f, r, i, d[n + 9], 4, -640364487), f, r, d[n + 12], 11, -421815835), m, f, d[n + 15], 16, 530742520), i, m, d[n + 2], 23, -995338651), r = _hashHelper_md5_ii(r, i = _hashHelper_md5_ii(i, m = _hashHelper_md5_ii(m, f, r, i, d[n + 0], 6, -198630844), f, r, d[n + 7], 10, 1126891415), m, f, d[n + 14], 15, -1416354905), i, m, d[n + 5], 21, -57434055), r = _hashHelper_md5_ii(r, i = _hashHelper_md5_ii(i, m = _hashHelper_md5_ii(m, f, r, i, d[n + 12], 6, 1700485571), f, r, d[n + 3], 10, -1894986606), m, f, d[n + 10], 15, -1051523), i, m, d[n + 1], 21, -2054922799), r = _hashHelper_md5_ii(r, i = _hashHelper_md5_ii(i, m = _hashHelper_md5_ii(m, f, r, i, d[n + 8], 6, 1873313359), f, r, d[n + 15], 10, -30611744), m, f, d[n + 6], 15, -1560198380), i, m, d[n + 13], 21, 1309151649), r = _hashHelper_md5_ii(r, i = _hashHelper_md5_ii(i, m = _hashHelper_md5_ii(m, f, r, i, d[n + 4], 6, -145523070), f, r, d[n + 11], 10, -1120210379), m, f, d[n + 2], 15, 718787259), i, m, d[n + 9], 21, -343485551), m = _hashHelper_safe_add(m, h), f = _hashHelper_safe_add(f, t), r = _hashHelper_safe_add(r, g), i = _hashHelper_safe_add(i, e) } return Array(m, f, r, i) }
    function _hashHelper_md5_cmn(d, _, m, f, r, i) { return _hashHelper_safe_add(_hashHelper_bit_rol(_hashHelper_safe_add(_hashHelper_safe_add(_, d), _hashHelper_safe_add(f, i)), r), m) }
    function _hashHelper_md5_ff(d, _, m, f, r, i, n) { return _hashHelper_md5_cmn(_ & m | ~_ & f, d, _, r, i, n) }
    function _hashHelper_md5_gg(d, _, m, f, r, i, n) { return _hashHelper_md5_cmn(_ & f | m & ~f, d, _, r, i, n) }
    function _hashHelper_md5_hh(d, _, m, f, r, i, n) { return _hashHelper_md5_cmn(_ ^ m ^ f, d, _, r, i, n) }
    function _hashHelper_md5_ii(d, _, m, f, r, i, n) { return _hashHelper_md5_cmn(m ^ (_ | ~f), d, _, r, i, n) }
    function _hashHelper_safe_add(d, _) { var m = (65535 & d) + (65535 & _); return (d >> 16) + (_ >> 16) + (m >> 16) << 16 | 65535 & m }
    function _hashHelper_bit_rol(d, _) { return d << _ | d >>> 32 - _ }


    export function stringReplaceAll(string: string, target: string, replacement: string): string {
        let result = string;
        try {
            return result.split(target).join(replacement);
        } catch (ex) {
            return string;
        }
    }
}

export namespace DOMhelpers {
    /**
     * Ein Fontawesome-Icon als Node erstellen und die Node erhalten.
     * @param classnames Fontawesome-Klassen (zB. 'fas fa-check') oder ggf. weitere (für Margins, Paddings, Farben, Größe)
    * @param space Optional: Icon links oder rechts mit Abstand versehen (true ist gleichbedeutend zu 'right'; falls keine Position angegeben wird, wird Position als 'first' angenommen)
     * @param parent Optional: Icon an ein Parent-Element platzieren
    * @param position Position, wenn das Icon gleich platziert wird (identisch zu dojos domConstruct.place)
    */
    export function createFontawesomeIcon(classnames: string, space?: boolean | "left" | "right", parent?: HTMLElement, position?: "first" | "last" | "only" | number): HTMLElement {
        if (!classnames) return null;

        let innerHTML = "";
        if (space === true || space == "right") {
            innerHTML = "&nbsp;";
            if (!position) position = "first";
        }

        let icon = domConstruct.create("i", { className: classnames, innerHTML: innerHTML ? innerHTML : "" });
        if (!position) position = "last";

        if ((position == "last" || (position as number) > 0) && (space == true || space == "left")) {
            icon.innerHTML = "";
            icon.className += " margin-left";
        }

        if (parent) {
            domConstruct.place(icon, parent, position);
        }

        return icon;
    }

    interface iconParseResult { cleanElement: string, iconClassResults: Array<string> };
    /**
     * Versucht aus einem string- oder HTML-Element Fontawesome-Icons herauszuparsen.
     *
     * @param source
     * @returns
     */
    export function tryParseIconFromLabel(source: string | HTMLElement): iconParseResult {
        if (!source) return null;

        let testElement: HTMLElement = null;
        try {

            if (typeof (source) == "string") {

                testElement = document.createElement("div");
                testElement.innerHTML = source;


            } else if (source instanceof HTMLElement) {
                testElement = source.cloneNode(true) as HTMLElement;
            } else {
                return null;
            }


            let results = testElement.getElementsByTagName("i");

            let result: iconParseResult = {
                cleanElement: null,
                iconClassResults: []
            };

            for (let i = 0; i < results.length; i++) {
                let child = results[i];
                if (child.classList.contains("fas") ||
                    child.classList.contains("far") ||
                    child.classList.contains("fa-regular") ||
                    child.classList.contains("fa-solid")) {

                    // treffer
                    result.iconClassResults.push(child.className);

                    testElement.removeChild(child);
                }
            }

            result.cleanElement = testElement.outerHTML;

            testElement.remove();

            return result;
        } catch (ex) {
            // naja gut, dann funktionierts halt ned...

        }

        return null;

    }
    /**
     * Setzt einen Standardstyle auf eine node, um Färbung von Hintergrund und Textfarbe für Light- und Darkmode zu vereinheitlichen.
     * Die Element-Klasse ist wenig spezifisch, d.h. andere Färbungen und CSS-Regeln überschreiben diese ggf. sehr einfach!
     * @param element
     * @param hexColorBG
     * @param hexColorTXT
     * @returns
     */
    export function setElementColor(element: HTMLElement, hexColorBG: string, hexColorTXT: string) {
        if (!element) return;
        if (!hexColorBG && !hexColorTXT) {
            domClass.remove(element, "coloredNode");
            element.style.removeProperty("--bgColor");
            element.style.removeProperty("--txtColor");

            return;
        }

        domClass.add(element, "coloredNode");

        if (hexColorBG) element.style.setProperty("--bgColor", hexColorBG);
        if (hexColorTXT) element.style.setProperty("--txtColor", hexColorTXT);
    }

}

export namespace TabbingHelpers {
    /**
    * Erzeugt einen neuen Tab mit der angegeben Konfiguration und gibt zurück, ob der Vorgang erfolgreich war.
    * @param config
    * @param activate nach Erstellung den neuen Tab aktivieren
    * @param replaceExistingTab Falls Tab mit gleichem Namen bereits vorhanden: diesen löschen und neu erstellen
    */
    export function createNewTabbedContent(config: Tabbing.TabPageDescriptor, bActivate?: boolean, bReplaceExistingTab?: boolean): boolean {
        if (window.tabManager) {
            return window.tabManager.createNewTab(config, bActivate, bReplaceExistingTab);
        } else {
            topic.publish("/Tabbing/NewTabRequest", config);
            if (bActivate) {
                requestTabSwitchGlobal(config.name);
            }
        }
    }
    //export function requestNewContextButton(config: Tabbing.ContextActionButtonRequest): void {
    //    topic.publish("/Tabbing/SwitchTabRequest", config);
    //}
    export function requestTabSwitchGlobal(tabname: string): void {
        topic.publish("/Tabbing/SwitchTabRequest", tabname);
    }
    export function requestTabTitleRename(tabname: string, newTitle: string): void {
        topic.publish("/Tabbing/ChangeTabTitleRequest", { tabname: tabname, newTitle: newTitle });
    }
    export function enableButton(buttonName: string, tabname: string, tabContainerName: string, bEnable: boolean = true): void {
        if (window.tabManager) {
            let tabContainer = window.tabManager.getTabContainerByName(tabContainerName);
            let tab = window.tabManager.getExistingTab(tabname, tabContainerName);

            if (tab && tabContainer) {
                tabContainer.enableButton(buttonName, tab.name, bEnable);
            }
        }
    }
    export function disableButton(buttonName: string, tabname: string, tabContainerName: string, bDisable: boolean = true): void {
        enableButton(buttonName, tabname, tabContainerName, bDisable === false);
    }



    function _getParentTabContainer(baseWidget: dijit._WidgetBase, maxLevels?: number): dijit._WidgetBase | MdsLax<InstanceType<typeof TabContainer>> {
        if (window.tabManager) {
            return window.tabManager.getTabContainerOfWidget(baseWidget, maxLevels);
        }


        try {
            let parent = baseWidget.getParent()/* as dijit._Widget*/;

            if (!parent) return null;

            if (parent["declaredClass"] == "TabContainer" && parent["containerClasp"]) {
                return parent;
            } else {
                return _getParentTabContainer(parent);
            }
        } catch (ex) {
            return null;
        }
    }
    /**
     * Prüft ob ein Tab mit name "tabname" schon existiert.
     * @param tabname
     * @param tabContainerName Falls angegeben: Suche auf diesen Container beschränken
     */
    export function isTabExisting(tabname: string, tabContainerName?: string): boolean {
        return window.tabManager.isTabExisting(tabname, tabContainerName);
    }
    /**
     * Aktiviert alle Tabs mit Namen "tabname" (oder beschränkt auf einen Container) und gibt zurück, ob der Vorgang erfolgreich war.
     * @param tabname
     * @param tabContainerName
     */
    export function activateTab(tabname: string, tabContainerName?: string): boolean {
        if (window.tabManager) {
            return window.tabManager.getExistingTab(tabname, tabContainerName, true) != null;
        } else {
            requestTabSwitchGlobal(tabname);
        }
    }
    export function getExistingTab(tabname: string, tabContainerName?: string): Tabbing.TabContainerPage {
        if (window.tabManager) {
            return window.tabManager.getExistingTab(tabname, tabContainerName);
        }
    }

    export function closeTab(tabname: string, tabContainerName: string): boolean {
        if (window.tabManager) {
            let tab = window.tabManager.getExistingTab(tabname, tabContainerName);
            let tabContainer = window.tabManager.getTabContainerByName(tabContainerName);

            if (tab && tabContainer) {
                return tabContainer.destroyPage(tab.name);
            }
        }

        return false;
    }
    export function getTabContainerName(widget: dijit._WidgetBase, maxLookupLevel?: number): string {
        if (!widget) return null;
        let tabContainer = _getParentTabContainer(widget, maxLookupLevel);
        if (tabContainer) return tabContainer["name"];

        return null;
    }
    // ---

}

export namespace DateHelpers {
    /**
     * Formatiert ein Datum zu Standard-Datumsformat (toLocaleString, je nach Region) oder beliebigen Formatstring mit dojoDate
     * @param date Datum
     * @param formatString Formatstring (optional), zB. yyyy-MM-dd HH:mm:ss
     */

    export function dateTimeToString(date: Date, formatString?: string): string {
        if (!date) return "";
        if (!date.getDate) return "";

        if (formatString/* || formatStringTime*/) {

            let options = {} as dojo.date.DateLocaleFormatOptions;

            if (formatString) {
                options.datePattern = formatString;
            }

            let includeDate = true;
            let includeTime = true;

            includeDate = formatString.indexOf("E") != -1 || formatString.indexOf("D") != -1 || formatString.indexOf("M") != -1 || formatString.indexOf("Y") != -1;
            includeTime = formatString.indexOf("H") != -1 || formatString.indexOf("m") != -1 || formatString.indexOf("s") != -1;

            if (includeDate === false && includeTime !== false) {
                options.selector = "time";
            } else if (includeDate === true && includeTime !== true) {
                options.selector = "date";
            }


            //if (formatStringTime) {
            //    options.timePattern = formatStringTime;
            //}

            //if (!formatStringDate && formatStringTime) {
            //    options.selector = "time";
            //} else if (formatStringDate && !formatStringTime) {
            //    options.selector = "date";
            //}
            return dojoDate.format(date, options);
        }

        return date.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });

        //let day = date.getDate();       // yields date
        //let month = date.getMonth() + 1;    // yields month (add one as '.getMonth()' is zero indexed)
        //let year = date.getFullYear();  // yields year
        //let hour = date.getHours();     // yields hours
        //let minute = date.getMinutes(); // yields minutes
        //let second = date.getSeconds(); // yields seconds

        //// After this construct a string with the above results as below
        //return _leadingZeros(day, 2) + "/" + _leadingZeros(month, 2) + "/" + year + " " + _leadingZeros(hour, 2) + ':' + _leadingZeros(minute, 2) + ':' + _leadingZeros(second, 2);

    }

    /**
     * Konvertiert Sekunden in ein besser nutzbares Format, zB. 2110320 wird zu 1T 10:12:00
     * Falls die Komponenten auf 'nonZero' gestellt sind, werden sie nur ausgegeben falls der Wert nicht 0 ist.
     * @param seconds
     * @param showWeeks
     * @param showDays
     * @param showHours
     * @param showMinutes
     * @param showSeconds
     */
    export function convertSeconds(seconds: number,
        showWeeks: boolean | "nonZero",
        showDays: boolean | "nonZero",
        showHours: boolean | "nonZero",
        showMinutes: boolean | "nonZero",
        showSeconds?: boolean | "nonZero"): string {
        let weeks = 0;
        let days = 0;
        let hours = 0;
        let minutes = 0;
        let isNeg = false;
        if (typeof (seconds) != "number") return "";
        if (seconds < 0) {
            isNeg = true;
            seconds = seconds * -1;
        }

        if (!!showWeeks) {
            let weekSeconds = 3600 * 24 * 7;
            while (seconds >= weekSeconds) {
                weeks++;
                seconds -= weekSeconds;
            }
        }

        if (!!showDays) {
            let daySeconds = 3600 * 24;
            while (seconds >= daySeconds) {
                days++;
                seconds -= daySeconds;
            }

            //    // falls die vorhergehende Komponente angezeigt wird, dann muss die nachfolgende upgegradet werden zu true statt nonzero!
            //    if (!!showWeeks && (weeks != 0 || showWeeks === true)) {
            //        showDays = true;
            //    }
        }

        if (!!showHours) {
            let hourSeconds = 3600;
            while (seconds >= hourSeconds) {
                hours++;
                seconds -= hourSeconds;
            }


            //    // falls die vorhergehende Komponente angezeigt wird, dann muss die nachfolgende upgegradet werden zu true statt nonzero!
            //    if (!!showWeeks && (days != 0 || showWeeks === true)) {
            //        showHours = true;
            //    }
        }

        if (!!showMinutes) {
            let minuteSeconds = 60;
            while (seconds >= minuteSeconds) {
                minutes++;
                seconds -= minuteSeconds;
            }
        }

        let result: string = "";

        //if (isNeg) {
        //    weeks = weeks ? weeks * -1 : 0;
        //    days = days ? days * -1 : 0;
        //    hours = hours ? hours * -1 : 0;
        //    minutes = minutes ? minutes * -1 : 0;
        //    seconds = seconds ? seconds * -1 : 0;
        //}

        if (!!showWeeks && (weeks != 0 || showWeeks === true)) result += weeks.toString() + "W ";
        if (!!showDays && (days != 0 || showDays === true)) result += days.toString() + "D ";
        if (!!showHours && (hours != 0 || showHours === true)) result += hours.toLocaleString(navigator.language, { minimumIntegerDigits: 1 }) + "h:";
        if (!!showMinutes && (minutes != 0 || showMinutes === true)) result += minutes.toLocaleString(navigator.language, { minimumIntegerDigits: 2 }) + "m:";
        if (!!showSeconds && (seconds != 0 || showSeconds === true)) result += seconds.toLocaleString(navigator.language, { minimumIntegerDigits: 2 }) + "s";

        result = result.trim();

        if (result.slice(-1) == ":") result = result.slice(0, result.length - 1);

        // Minuten und Sekunden werden als 00 angezeigt. aber wenn 00 steht und sonst garnix im String, dann diese auch kürzen auf eine Stelle:
        if (result == "00m") result = "0m";
        if (result == "00s") result = "0s";

        if (isNeg) result = "-" + result;

        return result;
    }

    interface RegexDatesResult { sourceString: string, formatType: RegexResultType, regex: RegExp };
    type RegexResultType = "ms" | "msTZ" | "sec" | "secTZ" | "min" | "minTZ" | "date";
    function _addRegexDateResultToArray(targetArray: Array<RegexDatesResult>, originalRegex: RegExp, newRegexResults: Array<string>, type: RegexResultType) {
        if (newRegexResults?.length && newRegexResults != null) {
            for (let result of newRegexResults) {
                targetArray.push({
                    formatType: type,
                    sourceString: result,
                    regex: originalRegex
                })
            }
        }
    }
    export function IsSameDay(date1: Date, date2: Date): boolean {
        if (!date1 || !date2) return false;

        let str1 = dateToString(date1, "yyyy-MM-dd");
        let str2 = dateToString(date2, "yyyy-MM-dd");

        return str1 == str2;
    }

   
    /**
     * Testet einen String auf Vorkommen von gängigen Zeit- oder Datumsangaben und gibt alle gefundenen Vorkommen samt Typ als Array retour.
     * Mit dem Ergebnis kann der originale String mittels Replace umformatiert werden.
     * Die Rückgabewerte sagen aus, in welchem Detailgrad die Zeitangabe formatiert ist (ms = inkl. Millisekunden, sec = inkl. Sekunden, min = inkl. Minuten, date = nur Datum, false = kein Datumsstring erkannt)
     * @param unknownString String der getestet wird
     * @returns TZ = mit Zeitzone ms = inkl. Millisekunden, sec = inkl. Sekunden, min = inkl. Minuten, date = nur Datum, false = kein Datumsstring erkannt
     */
    export function GetDatesInStr(unknownString: string): Array<RegexDatesResult> {
        if (!unknownString) return [];
        // 1. Versuch mit Millisekunden
        let result: Array<RegexDatesResult> = [];
        let regexResult = [];

        let regex = /\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d\.\d+([+-][0-2]\d:[0-5]\d|Z)/g;

        regexResult = regex.exec(unknownString);
        unknownString = unknownString.replace(regex, "");
        _addRegexDateResultToArray(result, regex, regexResult, "msTZ");


        // Millisekunden, keine Zeitzone:
        regex = /\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d\.\d+/g;
        regexResult = regex.exec(unknownString);
        unknownString = unknownString.replace(regex, "");
        _addRegexDateResultToArray(result, regex, regexResult, "ms");

        // 2. Versuch ohne Millisekunden
        regex = /\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d([+-][0-2]\d:[0-5]\d|Z)/g;
        regexResult = regex.exec(unknownString);
        unknownString = unknownString.replace(regex, "");
        _addRegexDateResultToArray(result, regex, regexResult, "secTZ");

        // ohne Millisekunden, keine Zeitzone:
        regex = /\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d/g;
        regexResult = regex.exec(unknownString);
        unknownString = unknownString.replace(regex, "");
        _addRegexDateResultToArray(result, regex, regexResult, "sec");

        // 3. Versuch ohne Sekunden
        regex = /\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d([+-][0-2]\d:[0-5]\d|Z)/g;
        regexResult = regex.exec(unknownString);
        unknownString = unknownString.replace(regex, "");
        _addRegexDateResultToArray(result, regex, regexResult, "minTZ");

        // keine Sekunden, keine Zeitzone:
        regex = /\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d/g;
        regexResult = regex.exec(unknownString);
        unknownString = unknownString.replace(regex, "");
        _addRegexDateResultToArray(result, regex, regexResult, "min");

        // ISO-Format yyyy-MM-dd
        regex = /(19|20)\d\d-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])/g;
        regexResult = regex.exec(unknownString);
        unknownString = unknownString.replace(regex, "");
        _addRegexDateResultToArray(result, regex, regexResult, "date");

        return result;
    }

    /**
     * Prüft mittels Regex ob ein String eines von mehreren gängigen Datums- und Zeitformaten enthält.
     * ACHTUNG: Diese Prüfung besagt nicht, dass der String ausschließlich aus EINEM Datumsstring besteht!
     */
    export function isStrContainingDateTime(unknownString: string): boolean {
        if (!unknownString) return false;
        let result = GetDatesInStr(unknownString);
        return result.length > 0;
    }

    export function getDiffMilliSeconds(earlierDate: Date, laterDate: Date): number {
        if (!earlierDate || !laterDate) return null;
        if (!earlierDate.getTime || !laterDate.getTime) return null;

        return laterDate.getTime() - earlierDate.getTime();
    }
    export function getDiffSeconds(earlierDate: Date, laterDate: Date): number {
        let ms = getDiffMilliSeconds(earlierDate, laterDate);
        if (ms != null) return Math.round(ms / 1000);
        return ms;
    }
    export function getDiffMinutes(earlierDate: Date, laterDate: Date): number {
        let s = getDiffSeconds(earlierDate, laterDate);
        if (s != null) return Math.ceil(s / 60);
        return s;

    }
    export function getDiffHours(earlierDate: Date, laterDate: Date): number {
        let m = getDiffMinutes(earlierDate, laterDate);
        if (m != null) return Math.ceil(m / 60);
        return m;
    }

    function _leadingZeros(num: number, size: number) {
        let s = "000000000" + num;
        return s.substring(s.length - size);
    }

    /**
     * Formatiert ein Datum zum regional geläufigen mittellangen String (DE: dd.MM.yyyy) ohne Zeitkomponente.
     * @param date Datum
     * @param formatString optionaler Formatstring mit gängigen Optionen für dojoDate (EEE, EEEE ergibt Di., Dienstag)
     */
    export function dateToString(date: Date, formatString?: string): string {
        if (!date) return "";
        if (!date.getDate) return "";

        let day = date.getDate();       // yields date
        let month = date.getMonth() + 1;    // yields month (add one as '.getMonth()' is zero indexed)
        let year = date.getFullYear();  // yields year

        if (formatString) {
            return dojoDate.format(date, { datePattern: formatString, selector: "date" });
        }
        else {
            return date.toLocaleDateString(undefined, { dateStyle: "medium" });
            return dojoDate.format(date, { selector: "date" });
        }
        // After this construct a string with the above results as below
        return _leadingZeros(day, 2) + "/" + _leadingZeros(month, 2) + "/" + year;
    }
    /**
     * Formatiert ein Datum in ein von JSON-Tools verwertbares Standardformat ohne Zeitzone.
     * Beispiel: 2022-05-01T23:59:59
     * @param date Datum
     */
    export function dateToLocalISOString(date: Date): string {
        if (!date) return "";
        if (typeof (date) != "object" || typeof (date.getDate) != "function") return "";

        let day = date.getDate();       // yields date
        let month = date.getMonth() + 1;    // yields month (add one as '.getMonth()' is zero indexed)
        let year = date.getFullYear();  // yields year
        let hour = date.getHours();     // yields hours
        let minute = date.getMinutes(); // yields minutes
        let second = date.getSeconds(); // yields seconds

        // After this construct a string with the above results as below
        return year + "-" + _leadingZeros(month, 2) + "-" + _leadingZeros(day, 2) + "T" + _leadingZeros(hour, 2) + ':' + _leadingZeros(minute, 2) + ':' + _leadingZeros(second, 2);

    }

    export function getCalendarWeekNumber(targetDate: Date): number {
        // aus dojo-Kalender, eine GEFIXTE Berechnung von Kalenderwochen, die nicht KW0 in manchen Jahren anzeigt!

        if (targetDate == null || !targetDate.getFullYear) return null;

        let copyDate = new Date(targetDate.valueOf());
        let weekCounter = 1;

        let startDate = new Date(copyDate.getFullYear(), 0, 1); // 1.1. des selben Jahres

        // auf den letzten Tag der Woche setzen:
        let dayOfWeek = startDate.getDay(); // Sonntag = 0, Montag = 1 ...
        if (dayOfWeek != 0) {
            // 0 - 0
            // 1 - +6 (Montag: 6 Tage dazuzählen)
            // 2 - +5 (Dienstag: 5 Tage dazuzählen)
            // ....
            let additionValue = 7 - dayOfWeek;
            startDate.setDate(startDate.getDate() + additionValue); // jetzt steht start auf jeden Fall am ersten Sonntag im Jahr, und das ist KW 1
        }

        // target auch auf den letzten Tag der Woche setzen:
        dayOfWeek = copyDate.getDay(); // Sonntag = 0, Montag = 1 ...
        if (dayOfWeek != 0) {
            // 0 - 0
            // 1 - +6
            // 2 - +5
            // ....
            let additionValue = 7 - dayOfWeek;
            copyDate.setDate(copyDate.getDate() + additionValue); // Target ist jetzt auf den folgenden Sonntag gestellt
        }



        // das compareDate einen Tag nach hinten setzen. es darf nie gleich wie der 1.1. sein
        let compareDate = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate() + 1);

        // immer 7 Tage dazuzählen, bis wir das target überholt haben
        // Ausnahme: wenn das Datum nicht im selben Jahr wie der Sonntag liegt, dann ist es schon KW 1 des Folgejahres. Schleife überspringen.
        while (compareDate.valueOf() < copyDate.valueOf() && copyDate.getFullYear() == compareDate.getFullYear()) {
            weekCounter++;
            compareDate = new Date(compareDate.getFullYear(), compareDate.getMonth(), compareDate.getDate() + 7, 0, 0);

            if (weekCounter >= 999) break;
        }
        let weekNumber = weekCounter;

        return weekNumber;
    }

    /**
     * Für Datumsproperties, die direkt aus einem c#-Controller serialisiert worden sind (beginnende mit /Date)
     * @param dateStr
     * @returns
     */
    export function cSharpStrToDate(dateStr: string): Date {
        if (!dateStr) return null;

        if (dateStr.indexOf("/Date(") == 0) {
            return new Date(parseInt(dateStr.substring(6)));
        }

        return null;
    }
}

export namespace ColorHelpers {
    export function colorNode(node: HTMLElement | Array<HTMLElement>, entityColor: more.EntityAPIColor, fallbackBGhex?: string, fallbackTXThex?: string): void {
        if (!node) return;

        if (lang.isArray(node)) {
            for (let element of node as Array<HTMLElement>) {
                colorNode(element, entityColor, fallbackBGhex, fallbackTXThex);
            }

            return;
        }

        let bgValue = null;
        let txtValue = null;

        if (entityColor) {
            if (entityColor.BackColor) {
                if (entityColor.BackColor.Hex) {
                    bgValue = entityColor.BackColor.Hex;
                }
            }

            if (entityColor.TextColor) {
                if (entityColor.TextColor.Hex) {
                    txtValue = entityColor.TextColor.Hex;
                }
            }
        }

        if (!bgValue && !!fallbackBGhex) {
            bgValue = fallbackBGhex;
        }
        if (!!bgValue && !txtValue && !fallbackTXThex) {
            txtValue = "var(--dark)";
            if (isDarkColor(bgValue)) {
                txtValue = "var(--bright)";
            }
        }
        if (!txtValue && !!fallbackTXThex) {
            txtValue = fallbackTXThex;
        }

        DOMhelpers.setElementColor(node, bgValue, txtValue);
    }
    /**
 * Prüft eine Hex-Farbe auf Helligkeit. Gibt true zurück, falls die Farbe "dunkel" ist und daher zB. als Kontrast eine helle Schriftfarbe nötig ist.
 * Gibt null zurück, wenn die Prüfung ungültig ist (zB. weil übergebene Farbe kein Hex-Wert)
 * @param hexColor
 * @param threshold Standard: 128. Je niedriger, desto dunkler muss die Farbe sein, um als dunkel zu gelten.
 */
    export function isDarkColor(hexColor: string, threshold?: number): boolean | null {
        let rgb = getRgbFromString(hexColor);

        if (rgb && rgb.length == 3) {
            return isDarkRGBColor(rgb[0], rgb[1], rgb[2], threshold);
        }
    }
    export function isDarkRGBColor(r: number, g: number, b: number, threshold?: number): boolean | null {
        let result = false;

        if (typeof (threshold) != "number") threshold = 128;

        try {
            let luma = 0.2126 * r + 0.7152 * g + 0.0722 * b; // per ITU-R BT.709

            if (luma < threshold) {
                result = true;
            }
        }

        catch (ex) {
            return null;
        }

        return result;
    }
    export function cssRgbToRgb(cssRgb: string): [r: number, g: number, b: number] {
        try {
            let m = cssRgb.match(/^rgb\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)$/i);
            if (m && m.length >= 4) {
                return [parseInt(m[1]), parseInt(m[2]), parseInt(m[3])];
            }
        } catch (ex) {
            console.warn("cannot parse rgb '" + cssRgb + "'");
        }

        return [] as any;
    }
    export function getRgbFromString(cssRgb: string): [r: number, g: number, b: number] {
        try {
            if (cssRgb.length > 8) { // rgb( 10, 10, 10) - als css?
                let rgb = cssRgbToRgb(cssRgb);
                if (rgb && rgb.length == 3) {
                    cssRgb = rgbColorToHex(rgb[0], rgb[1], rgb[2]);
                }
            }
            if (cssRgb.length >= 6) {
                let test = cssRgb.substring(0, 1);
                if (test == "#") {
                    cssRgb = cssRgb.substring(1);      // strip #
                }

                cssRgb = cssRgb.substring(0, 6); // nur die ersten 6 Stellen

                let rgb = parseInt(cssRgb, 16);   // convert rrggbb to decimal
                let r = (rgb >> 16) & 0xff;  // extract red
                let g = (rgb >> 8) & 0xff;  // extract green
                let b = (rgb >> 0) & 0xff;  // extract blue

                return [r, g, b];
            }
        } catch (ex) {
            console.warn("tried parsing rgb from invalid hexcolor '" + cssRgb + "'");
        }
        return [] as any;

    }
    export function rgbColorToHex(r: number, g: number, b: number): string {
        let hexR = r.toString(16);
        hexR = hexR.length == 1 ? "0" + hexR : hexR;

        let hexG = g.toString(16);
        hexG = hexG.length == 1 ? "0" + hexG : hexG;

        let hexB = b.toString(16);
        hexB = hexB.length == 1 ? "0" + hexB : hexB;

        return "#" + hexR + hexG + hexB;
    }

    export function getEntityAPIColor(colorStrBG?: string, colorStrTXT?: string): more.EntityAPIColor {
        let result: more.EntityAPIColor = null;

        let validColorBG: string = null;
        let validColorTXT: string = null;
        
        if (colorStrBG) {
            validColorBG = getHexColorFromString(colorStrBG, "BG");
        }
        if ( colorStrTXT) {
            validColorTXT = getHexColorFromString(colorStrTXT, "TXT");
        }

        if (validColorBG || validColorTXT) {
            result = {
                BackColor: {
                    IsNull: true,
                    Red: null,
                    Green: null,
                    Blue: null,
                    Hex: null
                },
                TextColor: {
                    IsNull: true,
                    Red: null,
                    Green: null,
                    Blue: null,
                    Hex: null
                },
                pitBGR: null
            };

            if (validColorBG) {
                
                result.BackColor.Hex = validColorBG;
                result.BackColor.IsNull = false;
                try {
                    let components = getRgbFromString(validColorBG);
                    result.BackColor.Red = components[0];
                    result.BackColor.Green = components[1];
                    result.BackColor.Blue = components[2];
                } catch (ex) {

                }
            }

            if (validColorTXT) {

                result.TextColor.Hex = validColorTXT;
                result.TextColor.IsNull = false;
                try {
                    let components = getRgbFromString(validColorTXT);
                    result.TextColor.Red = components[0];
                    result.TextColor.Green = components[1];
                    result.TextColor.Blue = components[2];
                } catch (ex) {

                }
            }
        }

        return result;
    }

    /**
     * Versucht aus einem Property (kann serialisierte pit-Farbe, Array mit 2 Komponenten oder ein einzelne Komponente sein) eine Farbe im HEX-Format zu ermitteln.
     * @param colorStrOrCssClass serialisierte pit-Farbe, Array mit 2 Komponenten oder ein einzelne Komponente
     */
    export function getHexColorFromProperty(colorvalue: string | more.EntityAPIColor | Array<string>, targetComponent: "BG" | "TXT" = "BG"): string {
        if (!colorvalue) return null;

        if (typeof (colorvalue) == "string") {
            return ColorHelpers.getHexColorFromString(colorvalue, targetComponent);
        } else if (typeof (colorvalue) == "object" && (colorvalue as Array<any>)?.length >= 2) {
            if (targetComponent == "BG" || !targetComponent) {
                return getHexColorFromProperty(colorvalue[0]);
            } else {
                return getHexColorFromProperty(colorvalue[1]);
            }
        } else if (typeof (colorvalue) == "object" && (colorvalue as more.EntityAPIColor)?.pitBGR) {
            return getHexColorFromString((colorvalue as more.EntityAPIColor)?.pitBGR, targetComponent);
        }

        return null;
    }
    export function parseLCH(cssString: string): { L: number, C: number, H: number, A: number } {
        // Matches integers and decimals, ignoring spaces, commas, slashes, or % signs
        const regexLCH = /([\d.]+)/g;
        const matches = cssString.match(regexLCH);

        if (!matches || matches.length < 3) {
            throw new Error("Invalid LCH string format - " + cssString);
        }

        return {
            L: parseFloat(matches[0]),
            C: parseFloat(matches[1]),
            H: parseFloat(matches[2]),
            // Optional: Alpha channel if present (e.g., lch(51 10 285 / 0.5))
            A: matches[3] ? parseFloat(matches[3]) : 1
        };
    }
    export function lchToHex(L: number, C: number, H: number, A?: number) {
        let lchString = "lch(" + L + " " + C + " " + H + " " + (A ? " / " + A : "") + ")";
        const ctx = document.createElement('canvas').getContext('2d');
        ctx.fillStyle = lchString;
        const color = ctx.fillStyle; // The browser automatically converts this to hex or rgb

        return getHexColorFromString(color);

        // 1. Convert LCH to CIELAB
        const hr = (H * Math.PI) / 180;
        const valueA = C * Math.cos(hr);
        const valueB = C * Math.sin(hr);

        // 2. Convert CIELAB to XYZ (using D65 white point)
        let y = (L + 16) / 116;
        let x = valueA / 500 + y;
        let z = y - valueB / 200;

        const epsilon = 216 / 24389;
        const kappa = 24389 / 27;

        x = Math.pow(x, 3) > epsilon ? Math.pow(x, 3) : (116 * x - 16) / kappa;
        y = L > kappa * epsilon ? Math.pow((L + 16) / 116, 3) : L / kappa;
        z = Math.pow(z, 3) > epsilon ? Math.pow(z, 3) : (116 * z - 16) / kappa;

        // D65 reference values
        x *= 0.95047;
        y *= 1.00000;
        z *= 1.08883;

        // 3. Convert XYZ to linear sRGB
        let rLin = x * 3.2406 + y * -1.5372 + z * -0.4986;
        let gLin = x * -0.9689 + y * 1.8758 + z * 0.0415;
        let bLin = x * 0.0557 + y * -0.2040 + z * 1.0570;

        // 4. Gamma correction (Linear sRGB to Standard sRGB)
        const toSRGB = (val) => {
            val = val > 0.0031308 ? 1.055 * Math.pow(val, 1 / 2.4) - 0.055 : 12.92 * val;
            return Math.max(0, Math.min(255, Math.round(val * 255))); // Clamp to 0-255
        };

        const r = toSRGB(rLin);
        const g = toSRGB(gLin);
        const b = toSRGB(bLin);

        // 5. Convert RGB integers to HEX string
        return "#" + [r, g, valueB].map(x => x.toString(16).padStart(2, '0')).join('');
    }

    /**
     * Versucht aus einem String eine Farbe im HEX-Format zu ermitteln.
     * @param colorStrOrCssClass Wert, der RGB (im CSS-Format 'rgb(r,g,b)' oder 'rgba(r,g,b,a)'), LCH eine CSS-Klasse, pit-BGR mit zwei Komponenten oder bereits ein Hex-String sein kann.
     */
    export function getHexColorFromString(colorStrOrCssClass: string, targetComponent: "BG" | "TXT" = "BG"): string {
        let result = null;
        let color = null;

        // pit-Colorstring?
        if (colorStrOrCssClass && typeof (colorStrOrCssClass) == "string") {
            if (colorStrOrCssClass.indexOf("lch", 0) != -1) {
                let lch = parseLCH(colorStrOrCssClass);
                if (lch) return lchToHex(lch.L, lch.C, lch.H, lch.A);
            }
            let colorParts = colorStrOrCssClass.split(":");
            if (colorParts.length == 2) {
                // pitbgr!!
                let colorStr = colorParts[targetComponent == "BG" ? 1 : 0];

                if (colorStr.length < 6) {
                    // Achtung, pit kürzt Nullen aus dem String
                    // 0000ff (also Rot, weil BGR) wird zu ff
                    while (colorStr.length < 6) colorStr = "0" + colorStr;

                }

                if (colorStr.length == 6) {
                    // das BGR umdrehen in RGB:
                    let colorRGB = colorStr.substring(4, 6) + colorStr.substring(2, 4) + colorStr.substring(0, 2);
                    result = getHexColorFromString(colorRGB, targetComponent);
                    if (result) return result;
                } else if (colorStr.length == 8) {
                    // bei pit-BGR ist das "kein Wert"
                    // falls TXT: prüfen ob BG hell oder dunkel ist
                    if (targetComponent == "TXT") {
                        if (isDarkColor(colorParts[1])) {
                            return rgbColorToHex(230, 230, 230);
                        } else {
                            return rgbColorToHex(20, 20, 20)
                        }
                    } else {
                        // keine Farbe bei BG? dann wirds schwieriger eine Farbe zu erfinden
                        return "#ffffff";
                    }
                }
            }
        }

        if (isCssClassExisting(colorStrOrCssClass)) {

            let testDiv = domConstruct.create("div", {
                style: {
                    "position": "fixed",
                },
                "className": colorStrOrCssClass
            }, document.body);

            let css = window.getComputedStyle(testDiv);

            color = css.backgroundColor;

            domConstruct.destroy(testDiv);
        } else if (colorStrOrCssClass) {
            let rgb = getRgbFromString(colorStrOrCssClass);
            if (rgb.length === 3) {
                return rgbColorToHex(rgb[0], rgb[1], rgb[2]);
            }

            let testDiv = domConstruct.create("div", {
                style: {
                    "backgroundColor": colorStrOrCssClass,
                }
            }, document.body);

            let css = window.getComputedStyle(testDiv);

            color = css.backgroundColor;

            domConstruct.destroy(testDiv);
        }


        if (color) {
            let rgb = getRgbFromString(color);
            if (rgb?.length == 3) {
                result = rgbColorToHex(rgb[0], rgb[1], rgb[2])
            }
        }

        return result;
    }
    function _getAllCssSelectors(): Array<string> {
        let result = [];
        for (let i = 0; i < document.styleSheets.length; i++) {
            try {

                let rules = document.styleSheets[i].cssRules;
                for (let x in rules) {

                    if (typeof rules[x]["selectorText"] == 'string') result.push(rules[x]["selectorText"]);
                }
            } catch (ex) {

            }
        }

        return result;
    }

    export function rgbaToHex(rgba: string, forceRemoveAlpha = false): string {
        return "#" + rgba.replace(/^rgba?\(|\s+|\)$/g, '') // Get's rgba / rgb string values
            .split(',') // splits them at ","
            .filter((string, index) => !forceRemoveAlpha || index !== 3)
            .map(string => parseFloat(string)) // Converts them to numbers
            .map((number, index) => index === 3 ? Math.round(number * 255) : number) // Converts alpha to 255 number
            .map(number => number.toString(16)) // Converts numbers to hex
            .map(string => string.length === 1 ? "0" + string : string) // Adds 0 when length of one number is 1
            .join("") // Puts the array to togehter to a string
    }
    function isCssClassExisting(selector): boolean {
        let selectors = _getAllCssSelectors();
        if (selector && selector.substr(0, 1) != ".") {
            selector = "." + selector;
        }
        return selectors.indexOf(selector) != -1;

    }
}

export namespace ToasterHelpers {
    /**
     * Wrapper um eine Success-Response einfacher anzuzeigen.
     * Standardmäßig wird ein Hinweis im Wortlaut von "gespeichert" oder "erfolgreich gespeichert" mit Bestätigungssymbol angezeigt.
     * Mit ownText kann optional eigener Text oder eine andere Übersetzung eingeschleust werden
    */
    export function showSaved(ownText?: string) {
        if (!ownText) ownText = "General__SaveSuccess"; // General_SavingChangesSuccessful
        showToast(ownText, toasterIcons.confirmCheck);
    }

    /**
     * Wrapper um eine Error-Response einfacher anzuzeigen.
     * Wenn kein title angegeben wird, wird die Errormessage als Titel benutzt (ACHTUNG bei potenziell langen errors!), ansonsten wird der Toast mit Titel und Message erzeugt.
    */
    export function showError(error: string | more.requestErrorMessage, title?: string, ID?: string): string {
        let _errorStr = error as string;
        if (typeof (error) == "object" && error) {
            _errorStr = error.Message;
        }
        if (!_errorStr) return;

        if (!title) {
            return showToast({
                title: _errorStr,
                iconClass: ToasterIcons.error,
                ID: ID,
                timeout: "permanent"
            });
        } else {
            return showToast({
                title: title,
                iconClass: ToasterIcons.error,
                ID: ID,
                message: _errorStr,
                timeout: "permanent"
            });
        }
    }
    /**
     * Fügt dem globalen Toaster eine neue Toastmessage hinzu. Rückgabe: ID des Toasts (wird generiert, falls nicht in den Optionen übergeben)
     * @param configOrText Konfigurationsobjekt. Wenn Message ausgefüllt ist, wird standardmäßig ein Callback mit einem GenericDialog erstellt, um die Message anzuzeigen.
     */
    export function showToast(configOrText: GlobalToastConfig | string, iconClass?: string, ID?: string): string {
        if (!configOrText) return null;

        if (typeof (configOrText) == "string") {
            let title = configOrText;
            configOrText = {
                title: title,
                iconClass: iconClass
            };
        }

        if (iconClass) configOrText.iconClass = iconClass;

        if (!configOrText.options) {
            configOrText.options = {

            };
        }
        if (ID) {
            configOrText.options.ID = ID;
        }
        if (!configOrText.options.ID) {
            configOrText.options.ID = "TM-" + (Math.random() * 100000).toString();
        }

        topic.publish("/Toaster/ShowToast", configOrText);


        return configOrText.options.ID;
    }
    export function removeToast(toastID: string): void {
        topic.publish("/Toaster/RemoveToast", toastID);
    }

    export enum ToasterIcons {
        warning = "fas fa-exclamation-triangle w3-text-orange",
        notification = "fas fa-exclamation-circle secondary",
        error = "fas fa-times-circle w3-text-red",
        confirmCheck = "fas fa-check w3-text-green",
        question = "fas fa-question w3-text-orange",
        info = "fas fa-info-circle w3-text-blue",
        close = "fas fa-times",
        save = "fas fa-save",
        undo = "fas fa-undo",
        redo = "fas fa-redo",
        hourglass = "fas fa-hourglass-half",
        hourglassStart = "fas fa-hourglass-start",
        hourglassEnd = "fas fa-hourglass-end ",
        delete = "fas fa-trash",
        none = ""
    }



}


export namespace FileHelpers {
    export function formatBytes(bytes: number, decimals = 2) {
        if (!+bytes) return '0 Bytes'

        const k = 1024;
        const dm = decimals < 0 ? 0 : decimals
        const sizes = ['Bytes', 'kB', 'MB', 'GB', 'TB', 'PiB', 'EiB', 'ZiB', 'YiB']

        const i = Math.floor(Math.log(bytes) / Math.log(k))

        return `${parseFloat((bytes / Math.pow(k, i)).toLocaleString(undefined, { minimumFractionDigits: dm, maximumFractionDigits: dm, minimumIntegerDigits: 1 }))} ${sizes[i]}`
    }

    export function getIconClassForFileType(extension: string, fallbackToDefault?: boolean) {

        if (extension) {
            extension = extension.toUpperCase();

            // IMAGE/PNG => PNG
            if (extension.indexOf("/") != -1) {
                let parts = extension.split("/");
                extension = parts.splice(-1)[0];
            }

            // .ABC => ABC
            if (extension.indexOf(".", 0) == 0) extension = extension.substring(1);

            // abc.name.PNG => PNG
            else if (extension.indexOf(".") != -1) {
                extension = extension.split(".").splice(-1)[0];
            }

        }
        switch (extension) {
            case "PNG":
            case "BMP":
            case "JPG":
            case "JPEG":
            case "HEIC":
            case "GIF":
            case "ICO":
                return "fas fa-file-image";
                break;

            case "PDF": return "fas fa-file-pdf";
                break;

            case "DOC":
            case "DOCX": return "fas fa-file-word";
                break;

            case "CSV": return "fas fa-file-csv";
                break;

            case "XLS":
            case "XLSX": return "fas fa-file-excel";
                break;

            case "TXT": return "fas fa-file-alt";
                break;

            case "7Z":
            case "ZIP": return "fas fa-file-archive";
                break;


            case "AVI":
            case "MKV":
            case "WMV":
            case "MOV": return "fas fa-file-video";
                break;

            default:
                if (fallbackToDefault === true) {
                    return "fas fa-file";
                }
                break;

        }


    }

    declare var require: any; //workaround weil typescript dumm ist

    const _mimeTypes: Record<string, string> = {
        "csv": "text/csv",
        "txt": "text/plain",
        "bmp": "image/bmp",
        "png": "image/png",
        "jpg": "image/jpeg",
        "jpeg": "image/jpeg",
        "gif": "image/gif",

        "avi": "video/x-msvideo",
        "webm": "video/webm",

        "doc": "application/msword",
        "pdf": "application/pdf",
        "rtf": "application/rtf",

        "json": "application/json",

        "xml": "application/xml",
        "zip": "application/zip"
    }

    export function getMimeTypeFromExtension(fileExtension: string): string {
        let octet = "application/octet-stream";

        if (!fileExtension) return octet;

        try {
            let parts = fileExtension.split('.');

            if (parts.length) {
                let ext = parts.pop();
                ext = ext.toLowerCase();

                let mime = _mimeTypes[ext];
                if (!mime) return octet;

                return mime;
            }
        } catch (ex) {

        }
        return octet;
    }

    /**
    * Startet den clientseitigen Download eines heruntergeladenen Fildescriptors bzw. öffnet die Preview (Feature: filePreview/useForGeneralDownloads)
    * @param data
    * @param bOverrideUsePreview Preview erzwingen oder verhindern, unabhängig vom vorhandenen Feature
    * @param alternativeFeaturePath Eigenen Featurepfad verwenden, von dem das Preview abhängig sein soll
    */
    export function provideFile(data: FileFinder.FileDescriptorWithBase64,
        bOverrideUsePreview?: boolean,
        alternativeFeaturePath?: string) {
        if (!data) return;
        if (!FeatureHelpers.hasFeature("System/UseControllerFileResultsForDownloads") && !data.base64Content) return;
        let filePreview = null;
        let featurepath = "filePreview/useForGeneralDownloads";
        if (alternativeFeaturePath) featurepath = alternativeFeaturePath;

        if (bOverrideUsePreview !== false && (FeatureHelpers.hasFeature(featurepath) || bOverrideUsePreview === true)) {
            try {
                filePreview = mdsRequire("../WidgetCollection/filePreview/filePreview");//NOTE -lazy loading vom widget, da ansonsten die webpack runtime zerstoert wird
            } catch (ex) {
                bOverrideUsePreview = false;
            }
        }

        if ((FeatureHelpers.hasFeature(featurepath) || bOverrideUsePreview === true) &&
            bOverrideUsePreview !== false) {
            let preview = new filePreview();
            preview.startup();

            if (preview["setFileAsDescriptor"]) {
                preview.setFileAsDescriptor(data, true);
            }
            else {
                let result = preview.setFile(data.filename, data.description, data.extension, data.base64Content, true);
            }

            //if (result instanceof imageViewer) {
            //    let imageview = result as imageViewer;
            //   imageview.
            //}
        } else {
            if (FeatureHelpers.hasFeature("System/UseControllerFileResultsForDownloads")) {
                // Anm: window.open mag Safari garnicht
                top.location.href = window.appRoot + StringHelpers.stringFormat("FileTransfer/Download?downloadKey={0}", encodeURIComponent(data.downloadKey));
                //window.open(window.appRoot + StringHelpers.stringFormat("FileTransfer/Download?downloadKey={0}", data.downloadKey), "_blank");

                //if (StringHelpers.isPitID(data.downloadKey)) {
                //    location.href = window.appRoot + StringHelpers.stringFormat("FileTransfer/Download?downloadKey={0}", data.downloadKey);
                //} else {
                //    location.href = window.appRoot + StringHelpers.stringFormat("FileTransfer/Download?downloadKey={0}", encodeURIComponent(data.downloadKey));
                //}
                //window.open(window.appRoot + StringHelpers.stringFormat("FileTransfer/Download?downloadKey={0}", data.downloadKey), "_blank");
                return;
            }
            //if (FeatureHelpers.hasFeature("System/UseLocalFilesForDownload")) {

            //let localResponse: FileFinder.FileDescriptorWithBase64 & {
            //    localFilepath: string,
            //    errorMessage: string
            //} = data as any;

            //if (localResponse.errorMessage) {
            //    alert("error at local file download: " + localResponse.errorMessage);
            //}
            //let downloadLink = document.createElement("a");

            //downloadLink.href = localResponse.localFilepath;//.replaceAll("\}", "\\");
            //downloadLink.download = data.filename + "." + data.extension;
            //downloadLink.innerText = data.filename + "." + data.extension;
            //downloadLink.target = "_blank";
            ////downloadLink.click();


            //}

            // Download zusammenstellen:
            //let a = domConstruct.create("a") as HTMLLinkElement;
            let mimeType = getMimeTypeFromExtension(data.extension);

            let byteCharacters = atob(data.base64Content);
            let byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            let byteArray = new Uint8Array(byteNumbers);
            let blob = new Blob([byteArray], { type: "data:" + mimeType + ';base64' });
            let blobURL = URL.createObjectURL(blob);

            let downloadLink = document.createElement("a");

            downloadLink.href = blobURL;
            downloadLink.download = data.filename + "." + data.extension;
            downloadLink.click();
            setTimeout(lang.hitch(domConstruct, "destroy", downloadLink), 120000);
            //a.href = downloadDescriptor;
            //domAttr.set(a, "download", data.filename + "." + data.extension);

            //a.click();

            //    const dl = document.createElement('a');
            //    let downloadDescriptor = "data:" + mimeType + ";base64," + data.base64Content;

            //    dl.setAttribute("download", ''); //zusaetzlich
            //    dl.href = downloadDescriptor;
            //    dl.download = data.filename + "." + data.extension;
            //    let clickEv = new MouseEvent("click", {
            //        bubbles: false,
            //        cancelable: false,
            //        view: window
            //    });
            //    try {
            //        //weil dl.click() evtl. auf manchen mobile Geraete nicht funktioniert...
            //        //dl.dispatchEvent(clickEv);
            //        throw ("success 1");

            //    } catch (ex) {
            //        alert("download 1 failed, trying blob; " + ex);
            //        // Variante 2, iphones

            //        let byteCharacters = atob(data.base64Content);
            //        let byteNumbers = new Array(byteCharacters.length);
            //        for (let i = 0; i < byteCharacters.length; i++) {
            //            byteNumbers[i] = byteCharacters.charCodeAt(i);
            //        }
            //        let byteArray = new Uint8Array(byteNumbers);
            //        let blob = new Blob([byteArray], { type: "data:" + mimeType + ';base64' });
            //        let blobURL = URL.createObjectURL(blob);
            //        try {
            //            //window.open(blobURL);
            //            throw ("success 2 (blob URL)");
            //        } catch (ex) {
            //            alert("download 2 failed, trying another a.click... " + ex);
            //            let dl2 = document.createElement("a");

            //            dl2.href = blobURL;
            //            dl2.download = data.filename + "." + data.extension;
            //            dl2.click();
            //            try {
            //                throw ("success 3");

            //            } catch (ex) {
            //                alert("download failed:" + ex);
            //            }
            //        }
            //    }
            //        setTimeout(lang.hitch(domConstruct, "destroy", dl), 10000);
        }
    }

}

export namespace ExtraHelpers {

    /**
     * Versucht zwei Werte jeglichen Typs zu vergleichen. Die values sollten vom gleichen Typ sein.
     * Unterschiedliche value-Types gelten als nicht gleich.
     * - string, boolean und numbers werden direkt verglichen
     * - null und null gelten als gleich
     * - undefined und undefined gelten als gleich
     * - Dates werden anhand ihres values verglichen
     * - objects werden stringifiziert und dann verglichen
     * Rückgabe true, wenn Werte gleich.
     * Fallback-Wert: false, falls Vergleiche fehlschlagen oder anderweitig nicht möglich.
     * @param value1
     * @param value2
     */
    export function isSameValue(value1, value2): boolean {
        if (value1 === undefined && value2 === undefined) return true;
        if (value1 === null && value2 == null) return true;

        if (typeof (value1) != typeof (value2)) return false;

        if (typeof (value1) == "string" || typeof (value1) == "number" || typeof (value1) == "boolean") {
            return value1 === value2;
        }

        if (typeof value1 == "object") {
            if (value1.valueOf && value1.getTime) { // Date?
                return value1.valueOf() == value2.valueOf();
            }
            try {
                let str1 = JSON.stringify(value1);
                let str2 = JSON.stringify(value2);

                return str1 == str2;
            } catch (ex) {

            }

        }

        return false;
    }

    /**
    * Lädt eine externe Ressource (JavaScript oder CSS) dynamisch in den DOM.
    *
    * @param url - Die URL der zu ladenden Ressource.
    * @param type - Der Typ der Ressource: 'js' für JavaScript oder 'css' für Stylesheets.
    * @returns Ein Promise, das bei erfolgreichem Laden aufgelöst wird oder beim Fehler abgelehnt wird.
    *
    * Das Skript wird asynchron geladen. Bei ungültigem Typ wird das Promise sofort mit einem Fehler abgelehnt.
    * window.appRoot wird NICHT automatisch zur URL hinzugefuegt!
    */
    export function sideLoadResource(url: string, type: 'js' | 'css'): Promise<void> {
        url = url.trim();
        type = type.toLowerCase() as any;
        if (typeof window.sideLoadedResources != 'object') {
            window.sideLoadedResources = {};
        }
        let key: string = encodeURIComponent(url); // encodeURIComponent weils feiner beim debuggen ist...
        if (key in window.sideLoadedResources) {
            return window.sideLoadedResources[key];
        }
        let prom: Promise<void> = new Promise((resolve, reject) => {
            switch (type) {
                case 'js':
                    // already loaded?
                    const existingScript = document.querySelector(`script[src="${url}"]`);
                    if (existingScript) {
                        resolve();
                        return;
                    }

                    const script = document.createElement('script');
                    script.src = url;
                    script.async = true;
                    script.onload = () => resolve();
                    script.onerror = () => {
                        script.remove();
                        const error = new Error(`Failed to load script: ${url}`);
                        console.error(error.message);
                        reject(error);
                    };
                    document.head.appendChild(script);
                    break;

                case 'css':
                    // already loaded?
                    const existingLink = document.querySelector(`link[href="${url}"]`);
                    if (existingLink) {
                        resolve();
                        return;
                    }

                    const link = document.createElement('link');
                    link.rel = 'stylesheet';
                    link.href = url;
                    link.onload = () => resolve();
                    link.onerror = () => {
                        link.remove();
                        const error = new Error(`Failed to load stylesheet: ${url}`);
                        console.error(error.message);
                        reject(error);
                    };
                    document.head.appendChild(link);
                    break;

                default:
                    const error = new Error(`Invalid resource type: ${type}. Expected 'js' or 'css'.`);
                    console.error(error.message);
                    reject(error);
                    break;
            }
        });
        if ((key in window.sideLoadedResources) == false) {
            window.sideLoadedResources[key] = prom;
            prom.finally(() => {
                if (key in window.sideLoadedResources) {
                    delete window.sideLoadedResources[key]; //memory cleanup + retrys on failed
                }
            });
        } else {
            return window.sideLoadedResources[key];
        }
        return prom;
    }

    export class AsyncOperationQueue<T = any> {

        static registerGlobaly<T = any>(queueName: string, maxConcurrentTasks: number = 1): AsyncOperationQueue<T> {
            //anscheinend keine race condition solange function synchronos ist...
            if (typeof window["globalAsyncOperationQueue"] != 'object') {
                window["globalAsyncOperationQueue"] = {};
            }
            if (typeof window["globalAsyncOperationQueue"][queueName] == 'undefined') {
                window["globalAsyncOperationQueue"][queueName] = new AsyncOperationQueue(maxConcurrentTasks);
            }
            return window["globalAsyncOperationQueue"][queueName];
        }
        private queue: Array<ThreadSafeQueueItem<T>> = [];
        private currentlyProcessing: number = 0;

        /**
         * Gibt an, wie viele Task in parralel verarbeitet werden koennen. Min. 1!
         */
        private maxConcurrentTasks: number;

        constructor(maxConcurrentTasks: number = 1) {
            this.setMaxConcurrentTasks(maxConcurrentTasks);
        }

        /**
         * Vararbeitet Operationen nacheinander (thread-safe)
         * @param asyncFunction Die Funktion, die ausgefuehrt wird. Muss async sein!
         * @param priority Wenn 'true', fuegt die Funktion zum anfang der Task Qeueu hinzu (wird als naechstes ausgefuehrt).
         * @returns Ein Promise der resolved wird, wenn die Queue die Operation ausgefuehrt hat
         */
        public enqueue<R>(asyncFunction: () => Promise<R> | any, priority: boolean = false): Promise<R> {
            if (typeof asyncFunction != 'function') {
                throw "invalid argument passed";
            }
            return new Promise<R>((resolve, reject) => {
                const queueItem: ThreadSafeQueueItem<R> = {
                    operation: asyncFunction,
                    resolve,
                    reject
                };
                if (priority == false) {
                    this.queue.push(queueItem as unknown as ThreadSafeQueueItem<T>);
                } else {
                    this.queue.unshift(queueItem as unknown as ThreadSafeQueueItem<T>);
                }
                this.processNext();
            });
        }

        private processNext(): void {
            // started neue tasks sollange wir gleichzeitig verarbeiten duerfen...
            while (this.currentlyProcessing < this.maxConcurrentTasks && this.queue.length > 0) {
                const item = this.queue.shift();
                if (!item) break;

                this.currentlyProcessing++;
                this.executeTask(item);
            }
        }

        private async executeTask(item: ThreadSafeQueueItem<any>): Promise<void> {
            try {
                const result = await item.operation();
                item.resolve(result);
            } catch (error) {
                item.reject(error);
            } finally {
                this.currentlyProcessing--;
                this.processNext();
            }
        }

        /**
         * Entfernt alle ausstehenden Operationen aus der Queue
         * @param error Optionaler error der in der Qeueu geworfen wird
         */
        public clear(error?: Error): void {
            const pendingItems = [...this.queue];
            this.queue = [];

            if (error) {
                pendingItems.forEach(item => item.reject(error));
            }
        }

        /**
         * Ob die Qeueu gerade beim verarbeiten ist
         */
        public isBusy(): boolean {
            return this.currentlyProcessing > 0 || this.queue.length > 0;
        }

        /**
         * Wie viele Operationen noch ausgefuehrt werden muessen
         */
        public GetQueueLength(): number {
            return this.queue.length;
        }

        /**
         * Wie viele Tasks gerade verarbeitet werden
         */
        public GetprocessingTasksCount(): number {
            return this.currentlyProcessing;
        }

        public setMaxConcurrentTasks(concurrentTasks: number) {
            if (concurrentTasks > 1) {
                this.maxConcurrentTasks = concurrentTasks;
            } else {
                this.maxConcurrentTasks = 1;
            }
        }
    }

    /**
     * Queue item interface to store operations and their resolution handlers
     */
    interface ThreadSafeQueueItem<T> {
        operation: () => Promise<T>;
        resolve: (value: T | PromiseLike<T>) => void;
        reject: (reason?: any) => void;
    }

}

export namespace PermissionHelper {

    /**
     * Gibt ein Storage Objekt zurueck das wir verwenden koennen, z.b. LocalStorage oder SessionStorage
     * Gedacht ist diese Funktion um in einem Storage Objekt Cache Daten zu speichern...
     * @returns Gibt Storage Objekt zurueck (entweder LocalStorage oder SessionStorage, jenachdem was verfuegbar ist) oder null wenn die Permissions fuer alles Fehlt...
     */
    export function getAvailableStorageMediumForCaching(storageTestOrder: Array<'localStorage' | 'sessionStorage'> = ['localStorage', 'sessionStorage']): Storage | null {
        const testKey = 'test-storage-availability';
        const storageTypes: ('localStorage' | 'sessionStorage')[] = storageTestOrder;

        for (const type of storageTypes) {
            try {
                const storage = window[type];
                storage.setItem(testKey, '1');
                storage.removeItem(testKey);
                return storage;
            } catch (e) {
            }
        }
        return null;
    }
}

export namespace DiagnosticHelper {
    function getStorageForUserName(): Storage | null {
        if (typeof window["diagnosticusernameStorage"] != 'undefined') {
            return window["diagnosticusernameStorage"];
        }

        let stor: Storage | null = PermissionHelper.getAvailableStorageMediumForCaching();
        if (stor) {
            window["diagnosticusernameStorage"] = stor;
            return stor;
        }
        return null;
    }

    /**
     * Laded den die User Datem von irgendend einem Storage damit
     * wir bei Page navigation auch immer einen User in den Logs haben
     * bzw. "UNKNOWN" wenn nicht angemeldet!
     */
    export function SetUserFromStorage() {
        let stor = getStorageForUserName();
        if (stor != null) {
            let usernameJsonStr = "";
            try {
                usernameJsonStr = stor.getItem("diagnostics_username_cache");
            } catch {
                return;
            }
            if (typeof usernameJsonStr == 'string') {
                let usernameJson: { username: string, identifier: string } = JSON.parse(usernameJsonStr);
                if (usernameJson.hasOwnProperty("username") && usernameJson.hasOwnProperty("identifier")) {
                    SetUser(usernameJson.username, usernameJson.identifier);
                }
            }
        }
    }

    function SetUserToStorage(username: string, identifier: string) {
        if (typeof username != 'string' && typeof identifier != 'string') {
            return;
        }
        let obj = { username: username, identifier: identifier };
        let objStr = JSON.stringify(obj);

        let stor = getStorageForUserName();
        if (stor != null) {
            try {
                stor.setItem("diagnostics_username_cache", objStr);
            } catch {
                return;
            }
        }
    }

    // wir fuehren die funktion on import aus damit wir sofort wenn verfuegbar einen usernamen haben
    DiagnosticHelper.SetUserFromStorage();

    /**
     * Setzt den Benutzer auf "UNKNOWN" zurück. Sollte aufgerufen werden wenn der User ausgeloggt wurde.
     * @returns {boolean} True wenn die Diagnostik API verfuegbar ist
     */
    export function ResetUser() {
        return SetUser("UNKNOWN", "UNKNOWN");
    }

    /**
     * Setzt den Benutzer mit angegebenem Benutzernamen und Identifier.
     * @param {string} username - Der Benutzername
     * @param {string} identifier - Die Pit-ID vom Benutzer
     * @returns {boolean} True wenn die Diagnostik API verfuegbar ist
     */
    export function SetUser(username: string, identifier: string) {
        if (!username) {
            username = "UNKNOWN";
        }
        if (!identifier) {
            identifier = "UNKNOWN";
        }
        SetUserToStorage(username, identifier);
        if (typeof (window as any)["diagnostic_fn_setUser"] == 'function') {
            (window as any)["diagnostic_fn_setUser"](username, identifier);
            return true;
        }
        return false;
    }

    /**
     * Zeigt einen Fehlerdialog mit den Details der übergebenen Exception an.
     * @param {Error} exceptionObject - Das Fehlerobjekt, um dass es geht
     * @returns {boolean} True wenn die Diagnostik API verfuegbar ist
     */
    export function ShowErrorFeedbackDlg(exceptionObject: Error) {
        if (typeof (window as any)["diagnostic_fn_tryShowErrorDlg"] == 'function') {
            (window as any)["diagnostic_fn_tryShowErrorDlg"](exceptionObject);
            return true;
        }
        return false;
    }

    /**
     * Sendet einen Fehler an den Diagnostik Server ohne Fehlerdialog
     * @param {Error} exceptionObject - Das Fehlerobjekt, um dass es geht
     * @returns {boolean} True wenn die Diagnostik API verfuegbar ist
     */
    export function CcaptureErrorSilently(exceptionObject: Error) {
        if (typeof (window as any)["diagnostic_fn_captureException"] == 'function') {
            (window as any)["diagnostic_fn_captureException"](exceptionObject);
            return true;
        }
        return false;
    }
}
export namespace AccessibilityLabelsHelper {

    /**
     * Bindet ein <label>-Element an ein <input>-Element (oder ein beliebiges Steuerelement) unter Verwendung der Standard for/id Beziehung.
     * @param widgetId Eine eindeutige ID für das Widget/die Komponente. Könnt als Präfix für generierte IDs verwendet.
     * @param inputElement Das Ziel-Input-HTMLElement.
     * @param idAttrValue Ziel-ID-Wert zur Verknüpfung von Label und Eingabe.
     * @param labelElement Das <label>-Element, das an das Input-Feld gebunden werden soll.
     * @param overrideIdAttrValue (default: true). Wenn true, wird der ID das Präfix widgetId im Format `${widgetId}__${idAttrValue}` vorangestellt.
     * @param overrideExistingId (default: false). Wenn true, wird die vorhandene ID des Eingabeelements ersetzt. Wenn false, bleibt die vorhandene ID erhalten.
     */
    export function LabelForInputBind(widgetId: string, inputElement: HTMLElement, idAttrValue: string, labelElement: HTMLElement, overrideIdAttrValue: boolean = true, overrideExistingId: boolean = false) {

        let targetAttrValue = idAttrValue;

        if (overrideIdAttrValue) targetAttrValue = `${widgetId}__${idAttrValue}`;

        domAttr.set(labelElement, "for", targetAttrValue);

        if (!domAttr.has(inputElement, "id") || overrideExistingId) domAttr.set(inputElement, "id", targetAttrValue);
    }

    /**
     * Verknüpft ein Input-Element mit einem oder mehreren Labels mithilfe des Attributs aria-labelledby.
     * @param labels Array von Labeldefinitionen: { labelElement: HTMLElement, labelIdValue: string } Jedes Element repräsentiert ein Label und seine Ziel-ID.
     * @param inputElement Das Ziel-Input-HTMLElement.
     * @param widgetId Eine eindeutige ID für das Widget/die Komponente. Könnt als Präfix für generierte IDs verwendet.
     * @param overrideIdAttrValue (default: true). Wenn true, wird der ID das Präfix widgetId im Format `${widgetId}__${idAttrValue}` vorangestellt.
     * @param overrideExistingId (default: false). Wenn true, wird die vorhandene ID des Eingabeelements ersetzt. Wenn false, bleibt die vorhandene ID erhalten.
     */
    export function InputLabelledbyBind(labels: { labelElement: HTMLElement, labelIdValue: string }[], inputElement: HTMLElement, widgetId: string, overrideIdAttrValue: boolean = true, overrideExistingId: boolean = false) {
        let labelledby = "";
        labels.forEach(label => {
            let targetIdValue = label.labelIdValue;

            if (overrideIdAttrValue) targetIdValue = `${widgetId}__${label.labelIdValue}`;

            if (!domAttr.has(label.labelElement, "id") || overrideExistingId) domAttr.set(label.labelElement, "id", targetIdValue);

            labelledby ? labelledby = `${labelledby} ${targetIdValue}` : labelledby = targetIdValue
        })
        domAttr.set(inputElement, "aria-labelledby", labelledby);
    }

    export function AddAriaLabel(element: HTMLElement, text: string) {

        domAttr.set(element, "aria-label", text);
    }
}

export {
    DOMhelpers as HtmlHelpers, // Legacy...
}
